import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut, getIdToken } from "firebase/auth";
import { auth, functions } from "../firebase-config";
import { httpsCallable } from "firebase/functions";

import {
  toggleUserStatus,
  toggleCourseAccess,
  updateCourseInFirestore,
  toggleAiAccess,
} from "../firebaseUtils";

import AddStudentForm from "../components/AddStudentForm";

const STUDENT_PLAN_OPTIONS = {
  "299-1-month": {
    name: "₱299 — 1 Month Promo",
    amount: 299,
    months: 1,
    color: "orange",
  },
  "299-2-months": {
    name: "₱299 — 2 Months Legacy Promo",
    amount: 299,
    months: 2,
    color: "yellow",
  },
  "599-6-months": {
    name: "₱599 — 6 Months Promo",
    amount: 599,
    months: 6,
    color: "green",
  },
  "999-1-year": {
    name: "₱999 — 1 Year Promo",
    amount: 999,
    months: 12,
    color: "blue",
  },
};

const CURRENT_UPGRADE_PLAN_CODES = [
  "299-1-month",
  "599-6-months",
  "999-1-year",
];

const addMonthsToDate = (dateValue, months) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  const originalDay = date.getDate();
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() + Number(months || 0));

  const lastDay = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0
  ).getDate();

  result.setDate(Math.min(originalDay, lastDay));
  return result;
};


const refreshAdminToken = async () => {
  if (!auth.currentUser) {
    throw new Error("Admin session expired. Please log in again.");
  }
  await getIdToken(auth.currentUser, true);
};

const runAdminWrite = async (fn) => {
  try {
    await refreshAdminToken();
    return await fn();
  } catch (err) {
    console.error("Admin operation failed:", err);
    alert("Admin operation failed. Check console for details.");
    return { success: false, message: err.message };
  }
};

export default function AdminDashboard({
  users = [],
  courses = [],
  videoLogs = [],
  videoSessions = [],
  loginLogs = [],
  ADMIN_UID,
  currentUserId,
  refreshAdminAnalyticsData = null,
  adminAnalyticsLoading = false,
  adminAnalyticsError = "",
  adminAnalyticsLastLoadedAt = null,
}) {
  const navigate = useNavigate();

  const [editingCourse, setEditingCourse] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [visibleStudentCount, setVisibleStudentCount] = useState(50);
  const [isSavingCourse, setIsSavingCourse] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [historySearch, setHistorySearch] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState("today");
  const [selectedAnalyticsDay, setSelectedAnalyticsDay] = useState(null);
  const [studentSort, setStudentSort] = useState("newest");
  const [bunnyAnalytics, setBunnyAnalytics] = useState(null);
  const [bunnyLoading, setBunnyLoading] = useState(false);
  const [bunnyError, setBunnyError] = useState("");
  const [bunnyRefreshNonce, setBunnyRefreshNonce] = useState(0);
  const [expandedHour, setExpandedHour] = useState(null);
  const [expandedHourStudent, setExpandedHourStudent] = useState(null);
  const [copiedActivityField, setCopiedActivityField] = useState("");
  const [expandedActivitySession, setExpandedActivitySession] = useState(null);
  const [selectedBandwidthStudent, setSelectedBandwidthStudent] = useState(null);
  const [selectedBandwidthVideo, setSelectedBandwidthVideo] = useState(null);
  const [adminView, setAdminView] = useState("home");
  const [activeAnalyticsPage, setActiveAnalyticsPage] = useState("analyticsCenter");
  const [loginWatchRange, setLoginWatchRange] = useState("today");
  const [loginWatchDate, setLoginWatchDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [videoOpenStats, setVideoOpenStats] = useState(null);
  const [videoOpenStatsError, setVideoOpenStatsError] = useState("");
  const [videoOpenStatsLoading, setVideoOpenStatsLoading] = useState(false);
  const [editingStudentAccount, setEditingStudentAccount] = useState(null);
  const [editStudentEmail, setEditStudentEmail] = useState("");
  const [editStudentPassword, setEditStudentPassword] = useState("");
  const [editStudentPlan, setEditStudentPlan] = useState("");
  const [editStudentSaving, setEditStudentSaving] = useState(false);
  const [expiredCleanupRunning, setExpiredCleanupRunning] = useState(false);
  const [studentHistorySessions, setStudentHistorySessions] = useState([]);
  const [studentHistoryLoading, setStudentHistoryLoading] = useState(false);
  const [studentHistoryError, setStudentHistoryError] = useState("");
  const [studentHistoryLoadedKey, setStudentHistoryLoadedKey] = useState("");

  // ✅ Performance gates: keep the home dashboard light.
  // Heavy analytics are only calculated/loaded when the matching analytics page is open.
  const isAnalyticsView = adminView === "analytics";
  const isAnalyticsCenterOpen = isAnalyticsView && activeAnalyticsPage === "analyticsCenter";
  const isWatchCostOpen = isAnalyticsView && activeAnalyticsPage === "watchCost";
  const isLifetimeOpensOpen = isAnalyticsView && activeAnalyticsPage === "lifetimeOpens";
  const isRecentActivityOpen = isAnalyticsView && activeAnalyticsPage === "recentActivity";
  const isLoginWatchHistoryOpen = isAnalyticsView && activeAnalyticsPage === "loginWatchHistory";
  const shouldUseVideoSessions =
    isAnalyticsView || selectedStudent || selectedBandwidthStudent || selectedBandwidthVideo || studentSort === "watchTime";
  const shouldUseVideoLogs =
    isAnalyticsView || selectedStudent || selectedBandwidthStudent || historySearch || historyFilter !== "all";
  const shouldLoadBunnyAnalytics = isAnalyticsCenterOpen || isWatchCostOpen;

  // ✅ Mobile performance: do not render hundreds of student cards at once.
  useEffect(() => {
    setVisibleStudentCount(50);
  }, [searchTerm, courseFilter, studentSort]);

  useEffect(() => {
    if (!currentUserId || currentUserId !== ADMIN_UID || !shouldLoadBunnyAnalytics) return;

    let cancelled = false;

    const loadBunnyAnalytics = async () => {
      try {
        setBunnyLoading(true);
        setBunnyError("");

        const getBunnyAnalytics = httpsCallable(functions, "getBunnyAnalytics");
        const response = await getBunnyAnalytics({
          range: analyticsRange,
          timezoneOffsetMinutes: new Date().getTimezoneOffset(),
        });

        if (!cancelled) {
          setBunnyAnalytics(response.data || null);
        }
      } catch (error) {
        console.error("Bunny analytics load failed:", error);
        if (!cancelled) {
          setBunnyError(error.message || "Failed to load Bunny analytics.");
        }
      } finally {
        if (!cancelled) {
          setBunnyLoading(false);
        }
      }
    };

    loadBunnyAnalytics();

    return () => {
      cancelled = true;
    };
  }, [analyticsRange, bunnyRefreshNonce, currentUserId, ADMIN_UID, shouldLoadBunnyAnalytics]);

  // ✅ Manual-only refresh: getVideoOpenStats is called only when the admin clicks Refresh Video Stats.
  const handleRefreshVideoOpenStats = async () => {
    if (!currentUserId || currentUserId !== ADMIN_UID || videoOpenStatsLoading) return;

    try {
      setVideoOpenStatsLoading(true);
      setVideoOpenStatsError("");

      const getVideoOpenStats = httpsCallable(
        functions,
        "getVideoOpenStats"
      );

      const response = await getVideoOpenStats({
        timezoneOffsetMinutes: new Date().getTimezoneOffset(),
      });

      setVideoOpenStats(response?.data || null);
    } catch (error) {
      console.error("Video open stats load failed:", error);
      setVideoOpenStatsError(
        error?.message || "Failed to load video open statistics."
      );
    } finally {
      setVideoOpenStatsLoading(false);
    }
  };

  const handleRefreshAllAnalytics = async () => {
    if (videoOpenStatsLoading || adminAnalyticsLoading) return;

    await Promise.all([
      handleRefreshVideoOpenStats(),
      typeof refreshAdminAnalyticsData === "function"
        ? refreshAdminAnalyticsData()
        : Promise.resolve(),
    ]);
  };

  const analyticsManualRefreshLoading =
    videoOpenStatsLoading || adminAnalyticsLoading;

  const formatAnalyticsLastLoaded = () => {
    if (!adminAnalyticsLastLoadedAt) return "Not loaded yet";
    return new Date(adminAnalyticsLastLoadedAt).toLocaleString();
  };

  const getTimestampSeconds = (timestamp) => {
    if (!timestamp) return 0;
    if (timestamp.seconds) return timestamp.seconds;
    if (timestamp.toDate) return Math.floor(timestamp.toDate().getTime() / 1000);
    return 0;
  };

  const formatFirestoreTime = (timestamp) => {
    if (!timestamp) return "Never";

    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }

    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }

    return "Never";
  };

  const formatLastLogin = (value) => {
  if (!value) return "Never";

  let date;

  if (value?.toDate) {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (isNaN(date.getTime())) {
    return "Never";
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

  const getDateMs = (value) => {
    if (!value) return 0;
    if (typeof value === "number") return value;
    if (value.toDate) return value.toDate().getTime();
    if (value.seconds) return value.seconds * 1000;

    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getStudentCreatedMs = (student) => {
    if (!student) return 0;

    return (
      getDateMs(student.createdAt) ||
      getDateMs(student.registeredAt) ||
      getDateMs(student.joinedAt) ||
      getDateMs(student.createdAtMs) ||
      getDateMs(student.lastLogin)
    );
  };

  const isNewStudent = (student) => {
    const createdMs = getStudentCreatedMs(student);
    if (!createdMs) return false;
    return Date.now() - createdMs <= 7 * 24 * 60 * 60 * 1000;
  };

  const formatCreatedDate = (student) => {
    const createdMs = getStudentCreatedMs(student);
    if (!createdMs) return "Unknown";
    return new Date(createdMs).toLocaleString();
  };

  const formatDuration = (seconds = 0) => {
    const total = Number(seconds || 0);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };


  const getSessionElapsedSeconds = (session = {}) => {
    const startedAt = normalizeSessionTimeMs(
      session.startedAtMs || session.startedAt
    );
    const endedAt = normalizeSessionTimeMs(
      session.endedAtMs || session.endedAt
    );

    if (!startedAt || !endedAt || endedAt < startedAt) return 0;

    return Math.max(0, Math.round((endedAt - startedAt) / 1000));
  };

  const formatSessionDateTime = (value) => {
    const timeMs = normalizeSessionTimeMs(value);
    if (!timeMs) return "Unknown";

    return new Date(timeMs).toLocaleString();
  };

  const formatMoney = (amount = 0) => {
    return `$${Number(amount || 0).toFixed(4)}`;
  };

  const formatGB = (amount = 0) => {
    return `${Number(amount || 0).toFixed(4)} GB`;
  };

  const formatNumber = (value = 0) => {
    return Number(value || 0).toLocaleString();
  };

  const formatBunnySyncTime = (value) => {
    if (!value) return "Never";
    return new Date(value).toLocaleString();
  };

  const getVideoInfo = (path = "") => {
    if (!path) {
      return {
        course: "Unknown Course",
        lesson: "Browsing course",
      };
    }

    const parts = path.split("/").filter(Boolean);

    let course = "Unknown Course";
    let lesson = "Browsing course";

    if (parts.length >= 2) {
      course = parts[1].replace(/-/g, " ");
    }

    if (parts.length >= 1) {
      lesson = parts[parts.length - 1]
        .replace(".mp4", "")
        .replace(/-/g, " ")
        .replace(/_/g, " ");
    }

    return {
      course,
      lesson,
    };
  };

  const isStudentOnline = (student) => {
    if (!student || student.online === false) return false;

    const seconds = getTimestampSeconds(student.lastSeen);
    if (!seconds) return false;

    const lastSeenMs = seconds * 1000;
    const now = Date.now();

    return now - lastSeenMs <= 45 * 1000;
  };

  const onlineStudentsFromUsers = users.filter(
    (u) => u.id !== ADMIN_UID && isStudentOnline(u)
  );

  const recentVideoLogs = isRecentActivityOpen
    ? [...videoLogs]
        .sort((a, b) => {
          const bTime = getTimestampSeconds(b.openedAt || b.watchedAt);
          const aTime = getTimestampSeconds(a.openedAt || a.watchedAt);
          return bTime - aTime;
        })
        .slice(0, 500)
    : [];

  const getSessionDedupKey = (session = {}) => {
    const studentKey = session.uid || session.email || "unknown";
    const videoKey = session.videoPath || session.lessonTitle || "unknown-video";
    const startedAt = Number(session.startedAtMs || session.endedAtMs || 0);

    // Some duplicate documents are not perfectly identical at the millisecond level.
    // Bucket the start time by minute so repeated saves from the same playback session
    // are treated as one analytics session, while still allowing a real re-watch later.
    const startedMinuteBucket = startedAt ? Math.floor(startedAt / 60000) : 0;
    const watchSeconds = Math.round(Number(session.watchSeconds || 0));
    const completionPercent = Math.round(Number(session.completionPercent || 0));

    return [
      studentKey,
      videoKey,
      startedMinuteBucket,
      watchSeconds,
      completionPercent,
    ].join("|");
  };

  const dedupeSessions = (sessions = []) => {
    const unique = new Map();

    sessions.forEach((session) => {
      const key = getSessionDedupKey(session);
      const existing = unique.get(key);

      if (!existing) {
        unique.set(key, session);
        return;
      }

      // Keep the richer record if one duplicate has more metadata.
      const existingScore =
        Number(Boolean(existing.endedAtMs)) +
        Number(Boolean(existing.videoPath)) +
        Number(Boolean(existing.lessonTitle)) +
        Number(existing.playCount || 0) +
        Number(existing.pauseCount || 0) +
        Number(existing.seekCount || 0);

      const currentScore =
        Number(Boolean(session.endedAtMs)) +
        Number(Boolean(session.videoPath)) +
        Number(Boolean(session.lessonTitle)) +
        Number(session.playCount || 0) +
        Number(session.pauseCount || 0) +
        Number(session.seekCount || 0);

      if (currentScore > existingScore) {
        unique.set(key, session);
      }
    });

    return [...unique.values()];
  };

  const videoSessionSource = shouldUseVideoSessions ? videoSessions : [];
  const videoLogSource = shouldUseVideoLogs ? videoLogs : [];

  const uniqueVideoSessions = dedupeSessions(videoSessionSource);
  const totalDuplicateSessions = Math.max(
    0,
    videoSessionSource.length - uniqueVideoSessions.length
  );

  const recentVideoSessions = [...uniqueVideoSessions]
    .sort((a, b) => (b.endedAtMs || 0) - (a.endedAtMs || 0))
    .slice(0, 500);

  const topVideoStudents = isLifetimeOpensOpen
  ? [...users]
      .filter((u) => u.id !== ADMIN_UID)
      .sort((a, b) => (b.videoOpenCount || 0) - (a.videoOpenCount || 0))
      .slice(0, 100)
  : [];

  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;

  const lifetimeVideoOpens = videoLogs.length;

  const totalWatchSeconds = uniqueVideoSessions.reduce(
    (sum, s) => sum + Number(s.watchSeconds || 0),
    0
  );

  const totalEstimatedGB = uniqueVideoSessions.reduce(
    (sum, s) => sum + Number(s.estimatedGB || 0),
    0
  );

  const totalEstimatedCost = uniqueVideoSessions.reduce(
    (sum, s) => sum + Number(s.estimatedCost || 0),
    0
  );

  const averageCompletion =
    uniqueVideoSessions.length > 0
      ? Math.round(
          uniqueVideoSessions.reduce(
            (sum, s) => sum + Number(s.completionPercent || 0),
            0
          ) / uniqueVideoSessions.length
        )
      : 0;

  const sessionsToday = uniqueVideoSessions.filter((session) => {
    const endedAt = Number(session.endedAtMs || 0);
    return endedAt >= oneDayAgo;
  });

  const estimatedCostToday = sessionsToday.reduce(
    (sum, s) => sum + Number(s.estimatedCost || 0),
    0
  );

  const estimatedGBToday = sessionsToday.reduce(
    (sum, s) => sum + Number(s.estimatedGB || 0),
    0
  );

  const normalizeSessionTimeMs = (value) => {
    if (!value) return 0;

    if (typeof value === "number") {
      // Unix timestamps below 1e12 are seconds; larger values are milliseconds.
      return value < 1e12 ? value * 1000 : value;
    }

    if (value?.toDate) {
      return value.toDate().getTime();
    }

    if (value?.seconds) {
      return Number(value.seconds) * 1000;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return numericValue < 1e12 ? numericValue * 1000 : numericValue;
    }

    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getSessionStartMs = (session) => {
    return normalizeSessionTimeMs(
      session?.startedAtMs ||
        session?.startedAt ||
        session?.endedAtMs ||
        session?.endedAt
    );
  };

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  const startOfLast7Days = new Date(startOfToday);
  startOfLast7Days.setDate(startOfLast7Days.getDate() - 6);

  const startOfLast30Days = new Date(startOfToday);
  startOfLast30Days.setDate(startOfLast30Days.getDate() - 29);

  const analyticsRangeOptions = [
    { key: "today", label: "📅 Today" },
    { key: "yesterday", label: "📅 Yesterday" },
    { key: "last7", label: "📅 Last 7 Days" },
    { key: "last30", label: "📅 Last 30 Days" },
    { key: "lifetime", label: "📅 Lifetime" },
  ];

  const getAnalyticsRangeLabel = (range) => {
    const match = analyticsRangeOptions.find((option) => option.key === range);
    return match ? match.label.replace("📅 ", "") : "Today";
  };

  const isSessionInAnalyticsRange = (session, range) => {
    const startedAt = getSessionStartMs(session);
    if (!startedAt) return false;

    if (range === "today") {
      return startedAt >= startOfToday.getTime() && startedAt < startOfTomorrow.getTime();
    }

    if (range === "yesterday") {
      return startedAt >= startOfYesterday.getTime() && startedAt < startOfToday.getTime();
    }

    if (range === "last7") {
      return startedAt >= startOfLast7Days.getTime() && startedAt < startOfTomorrow.getTime();
    }

    if (range === "last30") {
      return startedAt >= startOfLast30Days.getTime() && startedAt < startOfTomorrow.getTime();
    }

    return true;
  };

  const rawAnalyticsSessions = isAnalyticsCenterOpen
    ? videoSessions.filter((session) =>
        isSessionInAnalyticsRange(session, analyticsRange)
      )
    : [];

  const analyticsSessions = dedupeSessions(rawAnalyticsSessions);

  const getDayKey = (timeMs) => {
    const date = new Date(timeMs);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDayLabel = (dayKey) => {
    if (!dayKey) return "Selected day";
    const [year, month, day] = dayKey.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const analyticsDayRows = Object.values(
    analyticsSessions.reduce((acc, session) => {
      const startedAt = getSessionStartMs(session);
      if (!startedAt) return acc;

      const dayKey = getDayKey(startedAt);

      if (!acc[dayKey]) {
        acc[dayKey] = {
          dayKey,
          watchSeconds: 0,
          students: new Set(),
          sessions: 0,
          estimatedGB: 0,
          estimatedCost: 0,
        };
      }

      acc[dayKey].watchSeconds += Number(session.watchSeconds || 0);
      acc[dayKey].sessions += 1;
      acc[dayKey].estimatedGB += Number(session.estimatedGB || 0);
      acc[dayKey].estimatedCost += Number(session.estimatedCost || 0);

      if (session.uid || session.email) {
        acc[dayKey].students.add(session.uid || session.email);
      }

      return acc;
    }, {})
  )
    .map((row) => ({
      ...row,
      studentCount: row.students.size,
    }))
    .sort((a, b) => b.dayKey.localeCompare(a.dayKey));

  const activeHourlySessions = selectedAnalyticsDay
    ? analyticsSessions.filter((session) => {
        const startedAt = getSessionStartMs(session);
        return startedAt && getDayKey(startedAt) === selectedAnalyticsDay;
      })
    : analyticsSessions;

  const shouldShowDayPicker =
    analyticsRange === "last7" ||
    analyticsRange === "last30" ||
    analyticsRange === "lifetime";

  const analyticsDuplicateSessions = Math.max(
    0,
    rawAnalyticsSessions.length - analyticsSessions.length
  );

  const analyticsWatchSeconds = analyticsSessions.reduce(
    (sum, s) => sum + Number(s.watchSeconds || 0),
    0
  );

  const analyticsActiveStudents = new Set(
    analyticsSessions.map((s) => s.uid || s.email).filter(Boolean)
  ).size;

  const analyticsEstimatedGB = analyticsSessions.reduce(
    (sum, s) => sum + Number(s.estimatedGB || 0),
    0
  );

  const analyticsEstimatedCost = analyticsSessions.reduce(
    (sum, s) => sum + Number(s.estimatedCost || 0),
    0
  );

  const bunnyActualGB = Number(bunnyAnalytics?.totalBandwidthGB || 0);
  const bunnyActualCost = Number(bunnyAnalytics?.estimatedCost || 0);
  const bunnyOriginGB = Number(bunnyAnalytics?.totalOriginTrafficGB || 0);
  const bunnyRequests = Number(bunnyAnalytics?.totalRequestsServed || 0);
  const bunnyCacheHitRate = Number(bunnyAnalytics?.cacheHitRate || 0);
  const bunnyDifferenceGB = bunnyActualGB - analyticsEstimatedGB;
  const bunnyDifferenceCost = bunnyActualCost - analyticsEstimatedCost;

  const analyticsAverageCompletion =
    analyticsSessions.length > 0
      ? Math.round(
          analyticsSessions.reduce(
            (sum, s) => sum + Number(s.completionPercent || 0),
            0
          ) / analyticsSessions.length
        )
      : 0;

  const analyticsAverageWatchPerStudent =
    analyticsActiveStudents > 0
      ? Math.round(analyticsWatchSeconds / analyticsActiveStudents)
      : 0;

  const hourlyWatchAnalytics = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: `${String(hour).padStart(2, "0")}:00 - ${String(hour + 1).padStart(
      2,
      "0"
    )}:00`,
    watchSeconds: 0,
    students: new Set(),
    sessions: 0,
    estimatedGB: 0,
    estimatedCost: 0,
  }));

  activeHourlySessions.forEach((session) => {
    const startedAt = getSessionStartMs(session);
    if (!startedAt) return;

    const hour = new Date(startedAt).getHours();
    hourlyWatchAnalytics[hour].watchSeconds += Number(session.watchSeconds || 0);
    hourlyWatchAnalytics[hour].sessions += 1;
    hourlyWatchAnalytics[hour].estimatedGB += Number(session.estimatedGB || 0);
    hourlyWatchAnalytics[hour].estimatedCost += Number(session.estimatedCost || 0);

    if (session.uid || session.email) {
      hourlyWatchAnalytics[hour].students.add(session.uid || session.email);
    }
  });

  const hourlyWatchRows = hourlyWatchAnalytics.map((row) => ({
    ...row,
    studentCount: row.students.size,
  }));

  const getStudentsForHour = (hour) => {
    return Object.values(
      activeHourlySessions
        .filter((session) => {
          const startedAt = getSessionStartMs(session);
          if (!startedAt) return false;
          return new Date(startedAt).getHours() === hour;
        })
        .reduce((acc, session) => {
          const key = session.uid || session.email || "unknown";

          if (!acc[key]) {
            acc[key] = {
              uid: session.uid || "",
              email: session.email || "Unknown student",
              watchSeconds: 0,
              videosWatched: 0,
              completedVideos: 0,
              incompleteVideos: 0,
              completionTotal: 0,
              estimatedGB: 0,
              estimatedCost: 0,
            };
          }

          acc[key].watchSeconds += Number(session.watchSeconds || 0);
          acc[key].videosWatched += 1;
          acc[key].estimatedGB += Number(session.estimatedGB || 0);
          acc[key].estimatedCost += Number(session.estimatedCost || 0);
          acc[key].completionTotal += Number(session.completionPercent || 0);

          if (Number(session.completionPercent || 0) >= 90) {
            acc[key].completedVideos += 1;
          } else {
            acc[key].incompleteVideos += 1;
          }

          return acc;
        }, {})
    )
      .map((student) => ({
        ...student,
        averageCompletion:
          student.videosWatched > 0
            ? Math.round(student.completionTotal / student.videosWatched)
            : 0,
      }))
      .sort((a, b) => b.watchSeconds - a.watchSeconds);
  };

  const copyActivityText = async (text, label) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedActivityField(label);
      setTimeout(() => setCopiedActivityField(""), 2000);
    } catch (error) {
      console.error("Copy failed:", error);
      alert(`Could not copy ${label}. Please copy it manually.`);
    }
  };

  const copyStudentField = async (text, label) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      alert(`${label} copied!`);
    } catch (error) {
      console.error("Copy failed:", error);
      alert(`Could not copy ${label}. Please copy it manually.`);
    }
  };

  const emailStudent = (email) => {
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  const getSessionsForHourStudent = (hour, student) => {
    const studentKey = student.uid || student.email || "unknown";

    return activeHourlySessions
      .filter((session) => {
        const startedAt = getSessionStartMs(session);
        if (!startedAt) return false;

        const sameHour = new Date(startedAt).getHours() === hour;
        const sessionKey = session.uid || session.email || "unknown";

        return sameHour && sessionKey === studentKey;
      })
      .sort((a, b) => getSessionStartMs(a) - getSessionStartMs(b));
  };

  const formatSessionClockTime = (value) => {
    const timeMs = Number(value || 0);
    if (!timeMs) return "Unknown";

    return new Date(timeMs).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSessionFullTime = (value) => {
    const timeMs = Number(value || 0);
    if (!timeMs) return "Unknown";

    return new Date(timeMs).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };


  const getTimelineEventsForStudent = (sessions = []) => {
    return sessions
      .flatMap((session, index) => {
        const startedAt = Number(session.startedAtMs || session.endedAtMs || 0);
        const endedAt = Number(session.endedAtMs || 0);
        const completion = Number(session.completionPercent || 0);
        const completed = completion >= 90;
        const videoInfo = getVideoInfo(session.videoPath);
        const lessonTitle = session.lessonTitle || videoInfo.lesson || "Unknown lesson";
        const courseTitle = session.courseId || videoInfo.course || "Unknown course";
        const events = [];

        if (startedAt) {
          events.push({
            key: `${session.id || index}-started`,
            timeMs: startedAt,
            icon: "🎥",
            title: "Opened / Started watching",
            lessonTitle,
            courseTitle,
            details: `Session #${index + 1}`,
            statusClass: "text-cyan-300",
          });
        }

        if (endedAt) {
          events.push({
            key: `${session.id || index}-ended`,
            timeMs: endedAt,
            icon: completed ? "✅" : "❌",
            title: completed ? "Completed video" : `Stopped at ${completion}%`,
            lessonTitle,
            courseTitle,
            details: `${formatDuration(session.watchSeconds)} watched • ${formatMoney(session.estimatedCost)} • Pause ${session.pauseCount || 0} • Seek ${session.seekCount || 0}`,
            reason: session.reason || "Unknown",
            statusClass: completed ? "text-green-300" : "text-red-300",
          });
        }

        return events;
      })
      .sort((a, b) => a.timeMs - b.timeMs);
  };

  const peakAnalyticsHour = [...hourlyWatchRows].sort(
    (a, b) => b.watchSeconds - a.watchSeconds
  )[0];

  const topAnalyticsHours = [...hourlyWatchRows]
    .filter((row) => row.watchSeconds > 0)
    .sort((a, b) => b.watchSeconds - a.watchSeconds)
    .slice(0, 3);

  const maxHourlyWatchSeconds = Math.max(
    ...hourlyWatchRows.map((row) => row.watchSeconds),
    1
  );

  const analyticsStudentRows = Object.values(
    analyticsSessions.reduce((acc, session) => {
      const key = session.uid || session.email || "unknown";

      if (!acc[key]) {
        acc[key] = {
          uid: session.uid || "",
          email: session.email || "Unknown student",
          watchSeconds: 0,
          sessions: 0,
          estimatedGB: 0,
          estimatedCost: 0,
        };
      }

      acc[key].watchSeconds += Number(session.watchSeconds || 0);
      acc[key].sessions += 1;
      acc[key].estimatedGB += Number(session.estimatedGB || 0);
      acc[key].estimatedCost += Number(session.estimatedCost || 0);

      return acc;
    }, {})
  )
    .sort((a, b) => b.watchSeconds - a.watchSeconds)
    .slice(0, 15);

  const analyticsCourseRows = Object.values(
    analyticsSessions.reduce((acc, session) => {
      const courseName =
        session.courseId || getVideoInfo(session.videoPath).course || "Unknown Course";

      if (!acc[courseName]) {
        acc[courseName] = {
          course: courseName,
          watchSeconds: 0,
          students: new Set(),
          sessions: 0,
          estimatedGB: 0,
          estimatedCost: 0,
        };
      }

      acc[courseName].watchSeconds += Number(session.watchSeconds || 0);
      acc[courseName].sessions += 1;
      acc[courseName].estimatedGB += Number(session.estimatedGB || 0);
      acc[courseName].estimatedCost += Number(session.estimatedCost || 0);

      if (session.uid || session.email) {
        acc[courseName].students.add(session.uid || session.email);
      }

      return acc;
    }, {})
  )
    .map((row) => ({ ...row, studentCount: row.students.size }))
    .sort((a, b) => b.watchSeconds - a.watchSeconds)
    .slice(0, 15);

  const analyticsTopBandwidthVideos = Object.values(
    analyticsSessions.reduce((acc, session) => {
      const videoKey = session.videoPath || session.lessonTitle || "unknown-video";
      const videoInfo = getVideoInfo(session.videoPath);

      if (!acc[videoKey]) {
        acc[videoKey] = {
          videoKey,
          videoPath: session.videoPath || "",
          lessonTitle: session.lessonTitle || videoInfo.lesson || "Unknown video",
          course: session.courseId || videoInfo.course || "Unknown Course",
          watchSeconds: 0,
          students: new Set(),
          sessions: 0,
          estimatedGB: 0,
          estimatedCost: 0,
          completionTotal: 0,
        };
      }

      acc[videoKey].watchSeconds += Number(session.watchSeconds || 0);
      acc[videoKey].sessions += 1;
      acc[videoKey].estimatedGB += Number(session.estimatedGB || 0);
      acc[videoKey].estimatedCost += Number(session.estimatedCost || 0);
      acc[videoKey].completionTotal += Number(session.completionPercent || 0);

      if (session.uid || session.email) {
        acc[videoKey].students.add(session.uid || session.email);
      }

      return acc;
    }, {})
  )
    .map((row) => ({
      ...row,
      studentCount: row.students.size,
      averageCompletion:
        row.sessions > 0 ? Math.round(row.completionTotal / row.sessions) : 0,
    }))
    .sort((a, b) => b.estimatedGB - a.estimatedGB)
    .slice(0, 10);

  const analyticsTopBandwidthStudents = Object.values(
    analyticsSessions.reduce((acc, session) => {
      const key = session.uid || session.email || "unknown";

      if (!acc[key]) {
        acc[key] = {
          uid: session.uid || "",
          email: session.email || "Unknown student",
          watchSeconds: 0,
          sessions: 0,
          videos: new Set(),
          estimatedGB: 0,
          estimatedCost: 0,
          completionTotal: 0,
        };
      }

      acc[key].watchSeconds += Number(session.watchSeconds || 0);
      acc[key].sessions += 1;
      acc[key].estimatedGB += Number(session.estimatedGB || 0);
      acc[key].estimatedCost += Number(session.estimatedCost || 0);
      acc[key].completionTotal += Number(session.completionPercent || 0);

      if (session.videoPath || session.lessonTitle) {
        acc[key].videos.add(session.videoPath || session.lessonTitle);
      }

      return acc;
    }, {})
  )
    .map((row) => ({
      ...row,
      videoCount: row.videos.size,
      averageCompletion:
        row.sessions > 0 ? Math.round(row.completionTotal / row.sessions) : 0,
    }))
    .sort((a, b) => b.estimatedGB - a.estimatedGB)
    .slice(0, 10);

  const analyticsTopBandwidthCourses = [...analyticsCourseRows]
    .sort((a, b) => b.estimatedGB - a.estimatedGB)
    .slice(0, 10);

  const analyticsHighestCostSessions = [...analyticsSessions]
    .sort(
      (a, b) =>
        Number(b.estimatedCost || 0) - Number(a.estimatedCost || 0) ||
        Number(b.estimatedGB || 0) - Number(a.estimatedGB || 0)
    )
    .slice(0, 10);


  const bandwidthAlerts = (() => {
    const alerts = [];

    analyticsTopBandwidthStudents.forEach((student) => {
      const gb = Number(student.estimatedGB || 0);
      const sessions = Number(student.sessions || 0);

      if (gb >= 20 || sessions >= 60) {
        alerts.push({
          key: `student-${student.uid || student.email}`,
          type: "student",
          severity: gb >= 30 || sessions >= 100 ? "high" : "watch",
          icon: "👤",
          title: student.email || "Unknown student",
          subtitle: "High bandwidth student",
          metric: formatGB(gb),
          detail: `${formatMoney(student.estimatedCost)} • ${sessions} sessions • ${student.videoCount || 0} videos`,
          recommendation:
            gb >= 30 || sessions >= 100
              ? "High usage. Review this student's sessions and watch pattern."
              : "Watch this student if usage continues to increase.",
          actionLabel: "View Student Profile",
          action: () => setSelectedBandwidthStudent(student),
        });
      }
    });

    analyticsTopBandwidthVideos.forEach((video) => {
      const gb = Number(video.estimatedGB || 0);
      const sessions = Number(video.sessions || 0);

      if (gb >= 50 || sessions >= 100) {
        alerts.push({
          key: `video-${video.videoKey}`,
          type: "video",
          severity: gb >= 80 || sessions >= 150 ? "high" : "watch",
          icon: "🎥",
          title: video.lessonTitle || "Unknown video",
          subtitle: video.course || "High bandwidth video",
          metric: formatGB(gb),
          detail: `${formatMoney(video.estimatedCost)} • ${sessions} sessions • ${video.studentCount || 0} students • ${video.averageCompletion || 0}% avg completion`,
          recommendation:
            gb >= 80 || sessions >= 150
              ? "This video is a major bandwidth driver. Check who watched it."
              : "Monitor this lesson because it is using more traffic than usual.",
          actionLabel: "View Video Details",
          action: () => setSelectedBandwidthVideo(video),
        });
      }
    });

    analyticsHighestCostSessions.forEach((session, index) => {
      const gb = Number(session.estimatedGB || 0);
      const cost = Number(session.estimatedCost || 0);
      const completion = Number(session.completionPercent || 0);
      const watchSeconds = Number(session.watchSeconds || 0);
      const videoInfo = getVideoInfo(session.videoPath);

      if (gb >= 3 || cost >= 0.09) {
        alerts.push({
          key: `session-cost-${session.id || index}`,
          type: "session",
          severity: gb >= 5 || cost >= 0.15 ? "high" : "watch",
          icon: "💸",
          title: session.email || "Unknown student",
          subtitle: session.lessonTitle || videoInfo.lesson || "High cost session",
          metric: formatGB(gb),
          detail: `${formatMoney(cost)} • ${formatDuration(watchSeconds)} watched • ${completion}% completion`,
          recommendation:
            completion <= 10 && gb >= 3
              ? "High traffic with low completion. Possible download or unusual buffering."
              : "One session used unusually high bandwidth.",
          actionLabel: "View Session",
          action: () => setSelectedSession(session),
        });
      }
    });

    const lowCompletionHighTraffic = analyticsSessions.filter(
      (session) =>
        Number(session.estimatedGB || 0) >= 1 &&
        Number(session.completionPercent || 0) > 0 &&
        Number(session.completionPercent || 0) <= 10
    );

    if (lowCompletionHighTraffic.length >= 5) {
      const totalGB = lowCompletionHighTraffic.reduce(
        (sum, session) => sum + Number(session.estimatedGB || 0),
        0
      );

      alerts.push({
        key: "low-completion-high-traffic",
        type: "pattern",
        severity: totalGB >= 10 ? "high" : "watch",
        icon: "⚠️",
        title: "Low completion with high traffic",
        subtitle: "Possible downloading, repeated loading, or buffering issue",
        metric: formatGB(totalGB),
        detail: `${lowCompletionHighTraffic.length} sessions had 10% completion or less`,
        recommendation: "Check the highest cost sessions and students above.",
      });
    }

    return alerts
      .sort((a, b) => {
        const severityScore = { high: 2, watch: 1, normal: 0 };
        return (severityScore[b.severity] || 0) - (severityScore[a.severity] || 0);
      })
      .slice(0, 8);
  })();

  const highRiskAlertCount = bandwidthAlerts.filter(
    (alert) => alert.severity === "high"
  ).length;


  const selectedBandwidthVideoKey = selectedBandwidthVideo
    ? selectedBandwidthVideo.videoKey || selectedBandwidthVideo.videoPath || selectedBandwidthVideo.lessonTitle || "unknown-video"
    : "";

  const selectedBandwidthVideoSessions = selectedBandwidthVideo
    ? analyticsSessions
        .filter((session) => {
          const sessionVideoKey = session.videoPath || session.lessonTitle || "unknown-video";
          return sessionVideoKey === selectedBandwidthVideoKey;
        })
        .sort((a, b) => getSessionStartMs(b) - getSessionStartMs(a))
    : [];

  const selectedBandwidthVideoTotals = selectedBandwidthVideoSessions.reduce(
    (acc, session) => {
      acc.watchSeconds += Number(session.watchSeconds || 0);
      acc.sessions += 1;
      acc.estimatedGB += Number(session.estimatedGB || 0);
      acc.estimatedCost += Number(session.estimatedCost || 0);
      acc.completionTotal += Number(session.completionPercent || 0);

      if (session.uid || session.email) {
        acc.students.add(session.uid || session.email);
      }

      return acc;
    },
    {
      watchSeconds: 0,
      sessions: 0,
      estimatedGB: 0,
      estimatedCost: 0,
      completionTotal: 0,
      students: new Set(),
    }
  );

  const selectedBandwidthVideoAverageCompletion =
    selectedBandwidthVideoTotals.sessions > 0
      ? Math.round(
          selectedBandwidthVideoTotals.completionTotal /
            selectedBandwidthVideoTotals.sessions
        )
      : 0;

  const selectedBandwidthVideoStudentRows = Object.values(
    selectedBandwidthVideoSessions.reduce((acc, session) => {
      const key = session.uid || session.email || "unknown";

      if (!acc[key]) {
        acc[key] = {
          uid: session.uid || "",
          email: session.email || "Unknown student",
          watchSeconds: 0,
          sessions: 0,
          estimatedGB: 0,
          estimatedCost: 0,
          completionTotal: 0,
          completedSessions: 0,
        };
      }

      acc[key].watchSeconds += Number(session.watchSeconds || 0);
      acc[key].sessions += 1;
      acc[key].estimatedGB += Number(session.estimatedGB || 0);
      acc[key].estimatedCost += Number(session.estimatedCost || 0);
      acc[key].completionTotal += Number(session.completionPercent || 0);

      if (Number(session.completionPercent || 0) >= 90) {
        acc[key].completedSessions += 1;
      }

      return acc;
    }, {})
  )
    .map((row) => ({
      ...row,
      videoCount: 1,
      averageCompletion:
        row.sessions > 0 ? Math.round(row.completionTotal / row.sessions) : 0,
    }))
    .sort((a, b) => b.estimatedGB - a.estimatedGB);


  const selectedBandwidthStudentKey = selectedBandwidthStudent
    ? selectedBandwidthStudent.uid || selectedBandwidthStudent.email || "unknown"
    : "";

  const selectedBandwidthStudentSessions = selectedBandwidthStudent
    ? analyticsSessions
        .filter((session) => {
          const sessionKey = session.uid || session.email || "unknown";
          return sessionKey === selectedBandwidthStudentKey;
        })
        .sort((a, b) => getSessionStartMs(b) - getSessionStartMs(a))
    : [];

  const selectedBandwidthStudentProfile = selectedBandwidthStudent
    ? users.find((u) => selectedBandwidthStudent.uid && u.id === selectedBandwidthStudent.uid) ||
      users.find(
        (u) =>
          selectedBandwidthStudent.email &&
          u.email &&
          u.email === selectedBandwidthStudent.email
      ) ||
      null
    : null;

  const selectedBandwidthStudentLogs = selectedBandwidthStudent
    ? videoLogSource
        .filter(
          (log) =>
            (selectedBandwidthStudent.uid && log.uid === selectedBandwidthStudent.uid) ||
            (selectedBandwidthStudent.email && log.email === selectedBandwidthStudent.email)
        )
        .sort((a, b) => {
          const bTime = getTimestampSeconds(b.openedAt || b.watchedAt);
          const aTime = getTimestampSeconds(a.openedAt || a.watchedAt);
          return bTime - aTime;
        })
    : [];

  const selectedBandwidthStudentLatestLog =
    selectedBandwidthStudentLogs.length > 0 ? selectedBandwidthStudentLogs[0] : null;

  const selectedBandwidthStudentCountry =
    selectedBandwidthStudentProfile?.country ||
    selectedBandwidthStudentLatestLog?.country ||
    "Unknown";

  const selectedBandwidthStudentIp =
    selectedBandwidthStudentProfile?.ip || selectedBandwidthStudentLatestLog?.ip || "Unknown";

  const selectedBandwidthStudentVideoRows = Object.values(
    selectedBandwidthStudentSessions.reduce((acc, session) => {
      const videoKey = session.videoPath || session.lessonTitle || "unknown-video";
      const videoInfo = getVideoInfo(session.videoPath);

      if (!acc[videoKey]) {
        acc[videoKey] = {
          videoKey,
          videoPath: session.videoPath || "",
          lessonTitle: session.lessonTitle || videoInfo.lesson || "Unknown video",
          course: session.courseId || videoInfo.course || "Unknown Course",
          watchSeconds: 0,
          sessions: 0,
          estimatedGB: 0,
          estimatedCost: 0,
          completionTotal: 0,
          completedSessions: 0,
        };
      }

      acc[videoKey].watchSeconds += Number(session.watchSeconds || 0);
      acc[videoKey].sessions += 1;
      acc[videoKey].estimatedGB += Number(session.estimatedGB || 0);
      acc[videoKey].estimatedCost += Number(session.estimatedCost || 0);
      acc[videoKey].completionTotal += Number(session.completionPercent || 0);

      if (Number(session.completionPercent || 0) >= 90) {
        acc[videoKey].completedSessions += 1;
      }

      return acc;
    }, {})
  )
    .map((row) => ({
      ...row,
      averageCompletion:
        row.sessions > 0 ? Math.round(row.completionTotal / row.sessions) : 0,
    }))
    .sort((a, b) => b.estimatedGB - a.estimatedGB);

  const selectedBandwidthStudentTotals = selectedBandwidthStudentSessions.reduce(
    (acc, session) => {
      acc.watchSeconds += Number(session.watchSeconds || 0);
      acc.sessions += 1;
      acc.estimatedGB += Number(session.estimatedGB || 0);
      acc.estimatedCost += Number(session.estimatedCost || 0);
      acc.completionTotal += Number(session.completionPercent || 0);
      return acc;
    },
    {
      watchSeconds: 0,
      sessions: 0,
      estimatedGB: 0,
      estimatedCost: 0,
      completionTotal: 0,
    }
  );

  const selectedBandwidthStudentAverageCompletion =
    selectedBandwidthStudentTotals.sessions > 0
      ? Math.round(
          selectedBandwidthStudentTotals.completionTotal /
            selectedBandwidthStudentTotals.sessions
        )
      : 0;

  const selectedBandwidthStudentRiskLevel =
    selectedBandwidthStudentTotals.estimatedGB >= 30 ||
    selectedBandwidthStudentTotals.sessions >= 100
      ? "High"
      : selectedBandwidthStudentTotals.estimatedGB >= 10 ||
        selectedBandwidthStudentTotals.sessions >= 40
      ? "Watch"
      : "Normal";

  const topCostStudents = Object.values(
    uniqueVideoSessions.reduce((acc, session) => {
      const key = session.uid || session.email || "unknown";

      if (!acc[key]) {
        acc[key] = {
          uid: session.uid || "",
          email: session.email || "Unknown student",
          estimatedCost: 0,
          estimatedGB: 0,
          watchSeconds: 0,
          sessions: 0,
        };
      }

      acc[key].estimatedCost += Number(session.estimatedCost || 0);
      acc[key].estimatedGB += Number(session.estimatedGB || 0);
      acc[key].watchSeconds += Number(session.watchSeconds || 0);
      acc[key].sessions += 1;

      return acc;
    }, {})
  )
    .sort((a, b) => b.estimatedCost - a.estimatedCost)
    .slice(0, 10);

  const videoOpensToday = videoLogs.filter((log) => {
    const logTime = getTimestampSeconds(log.openedAt || log.watchedAt) * 1000;
    return logTime >= oneDayAgo;
  }).length;

  const videoOpensThisWeek = videoLogs.filter((log) => {
    const logTime = getTimestampSeconds(log.openedAt || log.watchedAt) * 1000;
    return logTime >= sevenDaysAgo;
  }).length;

  const accurateLifetimeVideoOpens =
    videoOpenStats?.lifetime ?? lifetimeVideoOpens;

  const accurateVideoOpensToday =
    videoOpenStats?.today ?? videoOpensToday;

  const accurateVideoOpensThisWeek =
    videoOpenStats?.thisWeek ?? videoOpensThisWeek;

  // ✅ One-pass student video stats.
  // This avoids filtering all videoLogs again for every student card.
  const studentVideoStatsMap = videoLogs.reduce((acc, log) => {
    const studentId = log.uid;
    if (!studentId) return acc;

    if (!acc[studentId]) {
      acc[studentId] = { logs: [], today: 0, week: 0, lifetime: 0 };
    }

    const logTimeMs = getTimestampSeconds(log.openedAt || log.watchedAt) * 1000;
    acc[studentId].lifetime += 1;

    if (shouldUseVideoLogs || selectedStudent?.id === studentId) {
      acc[studentId].logs.push(log);
    }

    if (logTimeMs >= oneDayAgo) acc[studentId].today += 1;
    if (logTimeMs >= sevenDaysAgo) acc[studentId].week += 1;

    return acc;
  }, {});

  Object.keys(studentVideoStatsMap).forEach((studentId) => {
    studentVideoStatsMap[studentId].logs.sort((a, b) => {
      const bTime = getTimestampSeconds(b.openedAt || b.watchedAt);
      const aTime = getTimestampSeconds(a.openedAt || a.watchedAt);
      return bTime - aTime;
    });
  });

  const getStudentVideoStats = (studentId, lifetimeFallback = 0) => {
    const stats = studentVideoStatsMap[studentId] || { logs: [], today: 0, week: 0, lifetime: 0 };

    return {
      logs: stats.logs,
      today: stats.today,
      week: stats.week,
      lifetime: lifetimeFallback || stats.lifetime,
    };
  };

  const selectedStudentStats = selectedStudent
    ? getStudentVideoStats(selectedStudent.id, selectedStudent.videoOpenCount || 0)
    : null;

  // ✅ One-pass student watch-time map.
  // This avoids scanning all video sessions for every student card while scrolling.
  const studentWatchSecondsMap = useMemo(() => {
    const map = {};

    uniqueVideoSessions.forEach((session) => {
      const watchSeconds = Number(session.watchSeconds || 0);

      if (session.uid) {
        map[session.uid] = (map[session.uid] || 0) + watchSeconds;
      }

      if (session.email) {
        map[session.email] = (map[session.email] || 0) + watchSeconds;
      }
    });

    return map;
  }, [uniqueVideoSessions]);

  const getStudentTotalWatchSeconds = (studentId, email = "") => {
    return Number(studentWatchSecondsMap[studentId] || studentWatchSecondsMap[email] || 0);
  };

  // ✅ User Directory full watch history.
  // Prefer the selected student's direct Firestore query so old sessions are
  // not limited by the global analytics loader's most-recent-session cap.
  const selectedStudentHistoryKey = selectedStudent
    ? selectedStudent.id || selectedStudent.uid || selectedStudent.email || ""
    : "";

  const selectedStudentSessions = selectedStudent
    ? dedupeSessions(
        studentHistoryLoadedKey === selectedStudentHistoryKey
          ? studentHistorySessions
          : uniqueVideoSessions.filter(
              (session) =>
                (selectedStudent.id && session.uid === selectedStudent.id) ||
                (selectedStudent.email &&
                  session.email &&
                  session.email.toLowerCase() ===
                    selectedStudent.email.toLowerCase())
            )
      ).sort((a, b) => getSessionStartMs(b) - getSessionStartMs(a))
    : [];

  const selectedStudentSessionTotals = selectedStudentSessions.reduce(
    (totals, session) => {
      totals.watchSeconds += Number(session.watchSeconds || 0);
      totals.estimatedGB += Number(session.estimatedGB || 0);
      totals.estimatedCost += Number(session.estimatedCost || 0);
      totals.completionTotal += Number(session.completionPercent || 0);
      totals.sessions += 1;
      return totals;
    },
    {
      watchSeconds: 0,
      estimatedGB: 0,
      estimatedCost: 0,
      completionTotal: 0,
      sessions: 0,
    }
  );

  const selectedStudentAverageCompletion =
    selectedStudentSessionTotals.sessions > 0
      ? Math.round(
          selectedStudentSessionTotals.completionTotal /
            selectedStudentSessionTotals.sessions
        )
      : 0;

  const selectedStudentSessionsToday = selectedStudentSessions.filter((session) => {
    const timeMs = getSessionStartMs(session);
    return timeMs >= startOfToday.getTime() && timeMs < startOfTomorrow.getTime();
  });

  const selectedStudentSessionsThisWeek = selectedStudentSessions.filter((session) => {
    const timeMs = getSessionStartMs(session);
    return timeMs >= startOfLast7Days.getTime() && timeMs < startOfTomorrow.getTime();
  });

  const filteredStudentHistory = selectedStudent
    ? selectedStudentSessions.filter((session) => {
        const sessionTime = getSessionStartMs(session);
        const search = historySearch.trim().toLowerCase();
        const info = getVideoInfo(session.videoPath);
        const lessonTitle = session.lessonTitle || info.lesson || "";
        const courseTitle = session.courseId || info.course || "";

        const matchesSearch =
          !search ||
          session.videoPath?.toLowerCase().includes(search) ||
          lessonTitle.toLowerCase().includes(search) ||
          courseTitle.toLowerCase().includes(search) ||
          session.ip?.toLowerCase().includes(search) ||
          session.country?.toLowerCase().includes(search);

        const matchesFilter =
          historyFilter === "all" ||
          (historyFilter === "today" &&
            sessionTime >= startOfToday.getTime() &&
            sessionTime < startOfTomorrow.getTime()) ||
          (historyFilter === "yesterday" &&
            sessionTime >= startOfYesterday.getTime() &&
            sessionTime < startOfToday.getTime()) ||
          (historyFilter === "week" &&
            sessionTime >= startOfLast7Days.getTime() &&
            sessionTime < startOfTomorrow.getTime()) ||
          (historyFilter === "last30" &&
            sessionTime >= startOfLast30Days.getTime() &&
            sessionTime < startOfTomorrow.getTime());

        return matchesSearch && matchesFilter;
      })
    : [];

  const getLoginWatchWindow = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (loginWatchRange === "today") {
      return { start: today.getTime(), end: tomorrow.getTime(), label: "Today" };
    }

    if (loginWatchRange === "yesterday") {
      return { start: yesterday.getTime(), end: today.getTime(), label: "Yesterday" };
    }

    if (loginWatchRange === "last7") {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { start: start.getTime(), end: tomorrow.getTime(), label: "Last 7 Days" };
    }

    if (loginWatchRange === "last30") {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { start: start.getTime(), end: tomorrow.getTime(), label: "Last 30 Days" };
    }

    if (loginWatchRange === "date") {
      const selected = new Date(`${loginWatchDate}T00:00:00`);
      const next = new Date(selected);
      next.setDate(next.getDate() + 1);
      return {
        start: selected.getTime(),
        end: next.getTime(),
        label: selected.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
      };
    }

    return {
      start: 0,
      end: Date.now() + 365 * 24 * 60 * 60 * 1000,
      label: "Lifetime",
    };
  };

  const loginWatchWindow = getLoginWatchWindow();

  const getUserLoginMs = (user) => {
    return (
      getDateMs(user?.lastLogin) ||
      getDateMs(user?.activeSessionStartedAt) ||
      getDateMs(user?.lastSeen)
    );
  };

  const getLoginLogMs = (log) => {
    return Number(log?.loginAtMs || 0) || getDateMs(log?.loginAt);
  };

  const isMsInLoginWatchWindow = (timeMs) => {
    if (!timeMs) return false;
    return timeMs >= loginWatchWindow.start && timeMs < loginWatchWindow.end;
  };

  const loginLogsForRange = isLoginWatchHistoryOpen
    ? loginLogs
        .filter((log) => isMsInLoginWatchWindow(getLoginLogMs(log)))
        .sort((a, b) => getLoginLogMs(b) - getLoginLogMs(a))
    : [];

  const loggedInUsersForRange = Object.values(
    loginLogsForRange.reduce((acc, log) => {
      const key = log.uid || log.email || log.id || "unknown";
      const matchingUser =
        users.find((u) => log.uid && u.id === log.uid) ||
        users.find((u) => log.email && u.email === log.email) ||
        null;

      if (!acc[key]) {
        acc[key] = {
          id: log.uid || matchingUser?.id || "",
          email: log.email || matchingUser?.email || "Unknown student",
          lastLogin: log.loginAt || matchingUser?.lastLogin || null,
          lastLoginMs: getLoginLogMs(log),
          country: log.country || matchingUser?.country || "Unknown",
          ip: log.ip || matchingUser?.ip || "Unknown",
          loginCount: 0,
        };
      }

      acc[key].loginCount += 1;

      return acc;
    }, {})
  ).sort((a, b) => Number(b.lastLoginMs || 0) - Number(a.lastLoginMs || 0));

  const fallbackLoggedInUsersForRange =
    loginLogs.length > 0
      ? []
      : users
          .filter((u) => u.id !== ADMIN_UID)
          .filter((u) => isMsInLoginWatchWindow(getUserLoginMs(u)))
          .sort((a, b) => getUserLoginMs(b) - getUserLoginMs(a));

  const displayedLoggedInUsersForRange =
    loggedInUsersForRange.length > 0
      ? loggedInUsersForRange
      : fallbackLoggedInUsersForRange;

  const loginWatchSessions = isLoginWatchHistoryOpen
    ? uniqueVideoSessions.filter((session) => {
        const startedAt = getSessionStartMs(session);
        return isMsInLoginWatchWindow(startedAt);
      })
    : [];

  const loginWatchVideoOpens = isLoginWatchHistoryOpen
    ? videoLogs.filter((log) => {
        const logTime = getTimestampSeconds(log.openedAt || log.watchedAt) * 1000;
        return isMsInLoginWatchWindow(logTime);
      })
    : [];

  const loginWatchActivityRecords =
    loginWatchSessions.length > 0
      ? loginWatchSessions.map((session) => ({
          ...session,
          sourceType: "session",
        }))
      : loginWatchVideoOpens.map((log) => ({
          id: log.id,
          uid: log.uid || "",
          email: log.email || "",
          videoPath: log.videoPath || "",
          lessonTitle: getVideoInfo(log.videoPath).lesson,
          courseId: log.courseId || getVideoInfo(log.videoPath).course,
          startedAtMs: getTimestampSeconds(log.openedAt || log.watchedAt) * 1000,
          endedAtMs: getTimestampSeconds(log.openedAt || log.watchedAt) * 1000,
          watchSeconds: 0,
          estimatedGB: 0,
          estimatedCost: 0,
          completionPercent: 0,
          country: log.country || "Unknown",
          ip: log.ip || "Unknown",
          sourceType: "open-log",
        }));

  const loginWatchUsingOpenLogsFallback =
    loginWatchSessions.length === 0 && loginWatchVideoOpens.length > 0;

  const loginWatchStudentRows = Object.values(
    loginWatchActivityRecords.reduce((acc, session) => {
      const key = session.uid || session.email || "unknown";
      const matchingUser =
        users.find((u) => session.uid && u.id === session.uid) ||
        users.find((u) => session.email && u.email === session.email) ||
        null;

      if (!acc[key]) {
        acc[key] = {
          uid: session.uid || matchingUser?.id || "",
          email: session.email || matchingUser?.email || "Unknown student",
          lastLogin:
            matchingUser?.lastLogin ||
            matchingUser?.activeSessionStartedAt ||
            matchingUser?.lastSeen ||
            null,
          country: matchingUser?.country || session.country || "Unknown",
          ip: matchingUser?.ip || session.ip || "Unknown",
          sessions: 0,
          watchSeconds: 0,
          estimatedGB: 0,
          estimatedCost: 0,
          completionTotal: 0,
          videos: new Set(),
          latestSession: session,
          sourceType: session.sourceType || "session",
        };
      }

      acc[key].sessions += 1;
      acc[key].watchSeconds += Number(session.watchSeconds || 0);
      acc[key].estimatedGB += Number(session.estimatedGB || 0);
      acc[key].estimatedCost += Number(session.estimatedCost || 0);
      acc[key].completionTotal += Number(session.completionPercent || 0);

      if (session.videoPath || session.lessonTitle) {
        acc[key].videos.add(session.videoPath || session.lessonTitle);
      }

      if (getSessionStartMs(session) > getSessionStartMs(acc[key].latestSession)) {
        acc[key].latestSession = session;
      }

      return acc;
    }, {})
  )
    .map((row) => ({
      ...row,
      videoCount: row.videos.size,
      averageCompletion:
        row.sessions > 0 ? Math.round(row.completionTotal / row.sessions) : 0,
    }))
    .sort((a, b) => b.watchSeconds - a.watchSeconds || b.sessions - a.sessions);

  const loginWatchTotalSeconds = loginWatchStudentRows.reduce(
    (sum, row) => sum + Number(row.watchSeconds || 0),
    0
  );

  const loginWatchTotalGB = loginWatchStudentRows.reduce(
    (sum, row) => sum + Number(row.estimatedGB || 0),
    0
  );

  const loginWatchTotalCost = loginWatchStudentRows.reduce(
    (sum, row) => sum + Number(row.estimatedCost || 0),
    0
  );

  const openStudentHistory = async (student) => {
    if (!student) return;

    const studentId = student.id || student.uid || "";
    const studentEmail = student.email || "";

    const fullStudent =
      users.find((user) => studentId && user.id === studentId) ||
      users.find(
        (user) =>
          studentEmail &&
          user.email &&
          user.email.toLowerCase() === studentEmail.toLowerCase()
      ) ||
      {
        ...student,
        id: studentId,
        uid: studentId,
        email: studentEmail || "Unknown student",
      };

    const historyKey =
      fullStudent.id || fullStudent.uid || fullStudent.email || "";

    // Open the modal immediately, then load only this student's sessions.
    setSelectedStudent(fullStudent);
    setHistoryFilter("all");
    setHistorySearch("");
    setSelectedSession(null);
    setStudentHistorySessions([]);
    setStudentHistoryLoadedKey("");
    setStudentHistoryError("");
    setStudentHistoryLoading(true);

    try {
      const getStudentVideoSessions = httpsCallable(
        functions,
        "getStudentVideoSessions"
      );

      const response = await getStudentVideoSessions({
        uid: fullStudent.id || fullStudent.uid || "",
        email: fullStudent.email || "",
      });

      const sessions = Array.isArray(response?.data?.sessions)
        ? response.data.sessions
        : [];

      setStudentHistorySessions(sessions);
      setStudentHistoryLoadedKey(historyKey);
    } catch (error) {
      console.error("Failed to load student watch history:", error);
      setStudentHistoryError(
        error?.message || "Failed to load this student's watch history."
      );
    } finally {
      setStudentHistoryLoading(false);
    }
  };

  const getStudentFromLog = (log) => {
    return (
      users.find((u) => u.id === log.uid) ||
      users.find((u) => u.email && log.email && u.email === log.email) ||
      null
    );
  };

  const getStudentPlanDisplay = (student) => {
    if (student?.subscriptionExempt === true) {
      return {
        label: "Exempt / Lesson Checking",
        className:
          "border-gray-500/50 bg-gray-500/15 text-gray-200",
      };
    }

    const planCode = student?.subscriptionPlan || "";

    const plans = {
      "299-1-month": {
        label: "₱299 / 1 Month",
        className:
          "border-orange-500/50 bg-orange-500/15 text-orange-300",
      },
      "299-2-months": {
        label: "₱299 / 2 Months Legacy",
        className:
          "border-yellow-500/50 bg-yellow-500/15 text-yellow-300",
      },
      "599-6-months": {
        label: "₱599 / 6 Months",
        className:
          "border-green-500/50 bg-green-500/15 text-green-300",
      },
      "999-1-year": {
        label: "₱999 / 1 Year",
        className:
          "border-blue-500/50 bg-blue-500/15 text-blue-300",
      },
    };

    return (
      plans[planCode] || {
        label: student?.subscriptionPlanName || "No Plan Recorded",
        className:
          "border-gray-600 bg-gray-800 text-gray-300",
      }
    );
  };

  const handleExportCurrentStudents = () => {
    const currentStudents = users
      .filter((user) => user.id !== ADMIN_UID)
      .map((user) => ({
        email: String(user.email || "").trim(),
        uid: user.id || "",
        role: user.role || "student",
        enabled: user.enabled === true ? "true" : "false",
        subscriptionPlan: user.subscriptionPlan || "",
        subscriptionPlanName: user.subscriptionPlanName || "",
        subscriptionStatus: user.subscriptionStatus || "",
        subscriptionExempt:
          user.subscriptionExempt === true ? "true" : "false",
      }))
      .sort((a, b) => a.email.localeCompare(b.email));

    const headers = [
      "Email",
      "UID",
      "Role",
      "Enabled",
      "Subscription Plan",
      "Subscription Plan Name",
      "Subscription Status",
      "Subscription Exempt",
    ];

    const escapeCsv = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const csv = [
      headers.map(escapeCsv).join(","),
      ...currentStudents.map((student) =>
        [
          student.email,
          student.uid,
          student.role,
          student.enabled,
          student.subscriptionPlan,
          student.subscriptionPlanName,
          student.subscriptionStatus,
          student.subscriptionExempt,
        ]
          .map(escapeCsv)
          .join(",")
      ),
    ].join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `vaapro-current-students-${
      new Date().toISOString().slice(0, 10)
    }.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setAlertMessage(
      `Exported ${currentStudents.length} current account(s).`
    );
  };

  const getStudentSubscriptionStartMs = (student) => {
    return (
      getDateMs(student?.createdAt) ||
      getDateMs(student?.registeredAt) ||
      getDateMs(student?.joinedAt) ||
      getDateMs(student?.subscriptionStartedAt) ||
      Number(student?.subscriptionStartedAtMs || 0)
    );
  };

  const getAvailableUpgradePlans = (student) => {
    if (!student || student.subscriptionExempt === true) return [];

    const currentMonths = Number(student.subscriptionMonths || 0);

    return CURRENT_UPGRADE_PLAN_CODES.filter((planCode) => {
      const plan = STUDENT_PLAN_OPTIONS[planCode];
      if (!plan) return false;

      if (!currentMonths) return true;
      return plan.months > currentMonths;
    });
  };

  const openEditStudentAccount = (student) => {
    setEditingStudentAccount(student);
    setEditStudentEmail(student?.email || "");
    setEditStudentPassword("");
    setEditStudentPlan("");
  };

  const closeEditStudentAccount = () => {
    if (editStudentSaving) return;

    setEditingStudentAccount(null);
    setEditStudentEmail("");
    setEditStudentPassword("");
    setEditStudentPlan("");
  };

  const handleSaveStudentAccount = async (event) => {
    event.preventDefault();

    if (!editingStudentAccount?.id) return;

    const cleanEmail = editStudentEmail.trim();

    if (!cleanEmail) {
      alert("Student email is required.");
      return;
    }

    if (editStudentPassword && editStudentPassword.length < 6) {
      alert("The new password must be at least 6 characters.");
      return;
    }

    setEditStudentSaving(true);

    const res = await runAdminWrite(async () => {
      const updateStudentAccountAndPlan = httpsCallable(
        functions,
        "updateStudentAccountAndPlan"
      );

      const response = await updateStudentAccountAndPlan({
        uid: editingStudentAccount.id,
        email: cleanEmail,
        newPassword: editStudentPassword,
        upgradePlan: editStudentPlan || "",
      });

      return response?.data || {
        success: false,
        message: "No response received from the account update.",
      };
    });

    setAlertMessage(res.message || "");
    setEditStudentSaving(false);

    if (res.success) {
      closeEditStudentAccount();
    }
  };

  const handleDeleteExpiredStudentsNow = async () => {
    if (expiredCleanupRunning) return;

    const confirmed = window.confirm(
      `⚠️ PERMANENT EXPIRED-STUDENT CLEANUP

This will immediately and permanently delete every expired student account, including:
• Firebase Authentication account
• User profile
• Lesson progress
• Video sessions and video-open logs
• Login history
• Bandwidth alerts
• Live-presence and dedupe records

Admin and subscription-exempt accounts will be skipped.

This action cannot be undone.

Continue now?`
    );

    if (!confirmed) return;

    const typedConfirmation = window.prompt(
      "Type DELETE EXPIRED to permanently remove all expired students:"
    );

    if (typedConfirmation !== "DELETE EXPIRED") {
      setAlertMessage("Expired-student cleanup cancelled.");
      return;
    }

    setExpiredCleanupRunning(true);
    setAlertMessage("Deleting expired students now...");

    const result = await runAdminWrite(async () => {
      const runExpiredStudentCleanupNow = httpsCallable(
        functions,
        "runExpiredStudentCleanupNow"
      );

      const response = await runExpiredStudentCleanupNow({});
      return response?.data || {
        success: false,
        message: "No response received from expired-student cleanup.",
      };
    });

    setExpiredCleanupRunning(false);

    if (result?.success === false) {
      setAlertMessage(
        result?.message || "Expired-student cleanup failed."
      );
      return;
    }

    const checked = Number(result?.checked || result?.checkedStudents || 0);
    const expired = Number(result?.expired || result?.expiredStudents || 0);
    const deleted = Number(result?.deleted || result?.deletedStudents || 0);
    const failed = Number(result?.failed || result?.failedStudents || 0);
    const skippedExempt = Number(
      result?.skippedExempt ||
        result?.skippedSubscriptionExempt ||
        result?.exemptSkipped ||
        0
    );

    setAlertMessage(
      result?.message ||
        `Expired cleanup complete — Checked: ${checked}, Expired: ${expired}, Deleted: ${deleted}, Failed: ${failed}, Exempt skipped: ${skippedExempt}.`
    );
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const handleToggleStatus = async (userId, enabled) => {
    const res = await runAdminWrite(() => toggleUserStatus(userId, enabled));
    setAlertMessage(res.message || "");
  };

  const handleToggleAccess = async (userId, courseId, hasAccess) => {
    const res = await runAdminWrite(() =>
      toggleCourseAccess(userId, courseId, hasAccess)
    );
    setAlertMessage(res.message || "");
  };

  const handleToggleAiAccess = async (userId, currentAccess) => {
    const res = await runAdminWrite(() =>
      toggleAiAccess(userId, currentAccess)
    );
    setAlertMessage(res.message || "");
  };

  const handleDeleteUser = async (userId, userEmail) => {
    const confirmDelete = window.confirm(
      `⚠️ PERMANENT DELETION

Delete ${userEmail} and ALL connected Firebase data?

This permanently removes:
• Firebase Authentication account
• User profile
• Lesson progress
• Video sessions and video-open logs
• Login history
• Bandwidth alerts
• Dedupe and live-presence records

This action cannot be undone.`
    );

    if (!confirmDelete) return;

    const typedConfirmation = window.prompt(
      `Type DELETE to permanently erase ${userEmail}:`
    );

    if (typedConfirmation !== "DELETE") {
      setAlertMessage("Deletion cancelled.");
      return;
    }

    const res = await runAdminWrite(async () => {
      const deleteStudentCompletely = httpsCallable(
        functions,
        "deleteStudentCompletely"
      );

      const result = await deleteStudentCompletely({
        uid: userId,
      });

      return {
        success: true,
        message:
          result?.data?.message ||
          `Successfully deleted ${userEmail} and all connected Firebase data.`,
      };
    });

    setAlertMessage(res.message || "");
  };

  const handleEditCourse = (course) => {
    if (!course) return;
    setEditingCourse(
      JSON.parse(JSON.stringify({ ...course, folders: course.folders || [] }))
    );
  };

  const handleCourseInputChange = (e) => {
    const { name, value } = e.target;
    setEditingCourse((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFolder = () => {
    setEditingCourse((prev) => ({
      ...prev,
      folders: [...(prev.folders || []), { title: "New Folder", lessons: [] }],
    }));
  };

  const handleDeleteFolder = (fIdx) => {
    if (!window.confirm("Delete folder?")) return;
    setEditingCourse((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.folders.splice(fIdx, 1);
      return copy;
    });
  };

  const handleFolderTitleChange = (fIdx, value) => {
    setEditingCourse((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.folders[fIdx].title = value;
      return copy;
    });
  };

  const handleAddLesson = (fIdx) => {
    setEditingCourse((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.folders[fIdx].lessons.push({ title: "New Lesson", videoUrl: "" });
      return copy;
    });
  };

  const handleDeleteLesson = (fIdx, lIdx) => {
    setEditingCourse((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.folders[fIdx].lessons.splice(lIdx, 1);
      return copy;
    });
  };

  const handleLessonChange = (fIdx, lIdx, key, value) => {
    setEditingCourse((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.folders[fIdx].lessons[lIdx][key] = value;
      return copy;
    });
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse?.id) return;
    setIsSavingCourse(true);
    const res = await runAdminWrite(() =>
      updateCourseInFirestore(editingCourse)
    );
    setAlertMessage(res.message || "");
    setIsSavingCourse(false);
  };

  const filteredUsers = [...users]
    .filter((u) => u.id !== ADMIN_UID)
    .filter((u) => {
      const search = searchTerm.toLowerCase().trim();
      const matchesSearch =
        !search ||
        u.email?.toLowerCase().includes(search) ||
        u.id?.toLowerCase().includes(search) ||
        u.country?.toLowerCase().includes(search);

      const matchesCourse =
        courseFilter === "all" || u.authorizedCourses?.includes(courseFilter);

      return matchesSearch && matchesCourse;
    })
    .sort((a, b) => {
      const aCreated = getStudentCreatedMs(a);
      const bCreated = getStudentCreatedMs(b);
      const aOnline = isStudentOnline(a) ? 1 : 0;
      const bOnline = isStudentOnline(b) ? 1 : 0;
      const aWatchSeconds = getStudentTotalWatchSeconds(a.id, a.email);
      const bWatchSeconds = getStudentTotalWatchSeconds(b.id, b.email);
      const aEmail = (a.email || "").toLowerCase();
      const bEmail = (b.email || "").toLowerCase();

      if (studentSort === "oldest") return aCreated - bCreated;
      if (studentSort === "online") return bOnline - aOnline;
      if (studentSort === "offline") return aOnline - bOnline;
      if (studentSort === "videoOpens") {
        return Number(b.videoOpenCount || 0) - Number(a.videoOpenCount || 0);
      }
      if (studentSort === "watchTime") return bWatchSeconds - aWatchSeconds;
      if (studentSort === "az") return aEmail.localeCompare(bEmail);
      if (studentSort === "za") return bEmail.localeCompare(aEmail);

      return bCreated - aCreated;
    });

  const visibleFilteredUsers = filteredUsers.slice(0, visibleStudentCount);
  const hasMoreStudents = visibleFilteredUsers.length < filteredUsers.length;

  if (!currentUserId || currentUserId !== ADMIN_UID) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white font-bold">
        ACCESS DENIED.
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-900 text-white p-4 sm:p-6 md:p-10 font-sans">
      <header className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-8 border-b border-gray-700 pb-4">
        <div>
          <h1 className="text-3xl font-extrabold text-orange-500 tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-gray-400 text-sm">VA Academy Control Center</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/admin/student-preview")}
            className="bg-blue-600 hover:bg-blue-700 px-5 py-2 rounded-lg font-bold transition-all shadow-md"
          >
            🎓 Student Dashboard
          </button>

          <button
            type="button"
            onClick={handleExportCurrentStudents}
            className="bg-emerald-600 hover:bg-emerald-700 px-5 py-2 rounded-lg font-bold transition-all shadow-md"
          >
            📥 Export Current Students
          </button>

          <button
            type="button"
            onClick={handleDeleteExpiredStudentsNow}
            disabled={expiredCleanupRunning}
            className={`px-5 py-2 rounded-lg font-bold transition-all shadow-md ${
              expiredCleanupRunning
                ? "bg-gray-600 cursor-not-allowed opacity-70"
                : "bg-orange-600 hover:bg-orange-700"
            }`}
          >
            {expiredCleanupRunning
              ? "Deleting Expired Students..."
              : "🧹 Delete Expired Students Now"}
          </button>

          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 px-5 py-2 rounded-lg font-bold transition-all shadow-md"
          >
            Logout
          </button>
        </div>
      </header>


      {adminView !== "home" && (
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-gray-800 border border-gray-700 rounded-2xl p-4">
          <div>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Admin Dashboard</p>
            <h2 className="text-2xl font-black text-white">
              {activeAnalyticsPage === "analyticsCenter" && "📈 Analytics Center"}
              {activeAnalyticsPage === "watchCost" && "📊 Watch Analytics & Bunny Cost"}
              {activeAnalyticsPage === "lifetimeOpens" && "📈 Lifetime Video Opens"}
              {activeAnalyticsPage === "recentActivity" && "🎥 Recent Video Activity"}
              {activeAnalyticsPage === "loginWatchHistory" && "👥 Login & Watch History"}
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              Dedicated analytics view inside AdminDashboard.jsx only.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleRefreshAllAnalytics}
              disabled={analyticsManualRefreshLoading}
              className={`px-4 py-2 rounded-lg text-sm font-black border transition w-fit ${
                analyticsManualRefreshLoading
                  ? "bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
                  : "bg-blue-900/40 border-blue-600/60 text-blue-200 hover:bg-blue-700"
              }`}
            >
              {analyticsManualRefreshLoading
                ? "Refreshing..."
                : "🔄 Refresh Analytics Data"}
            </button>

            <button
              onClick={() => setAdminView("home")}
              className="bg-gray-700 hover:bg-gray-600 px-5 py-2 rounded-lg text-sm font-black transition w-fit"
            >
              ← Back to Dashboard Home
            </button>
          </div>
        </div>
      )}

      {adminView === "home" && (
        <>
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-xs text-gray-400">
            Analytics data is manual only. Nothing refreshes in the background.
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Last loaded: {formatAnalyticsLastLoaded()}
          </p>
        </div>

        <button
          type="button"
          onClick={handleRefreshAllAnalytics}
          disabled={analyticsManualRefreshLoading}
          className={`px-4 py-2 rounded-lg text-xs font-black border transition w-fit ${
            analyticsManualRefreshLoading
              ? "bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
              : "bg-blue-900/40 border-blue-600/60 text-blue-200 hover:bg-blue-700"
          }`}
        >
          {analyticsManualRefreshLoading
            ? "Refreshing..."
            : "🔄 Refresh Analytics Data"}
        </button>
      </div>

      {adminAnalyticsError && (
        <div className="mb-4 rounded-xl border border-red-700/50 bg-red-950/30 px-4 py-3 text-xs text-red-300">
          {adminAnalyticsError}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
        <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs uppercase font-bold">
            Total Students
          </p>
          <p className="text-2xl font-black text-white">{users.length - 1}</p>
        </div>

        <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs uppercase font-bold">
            Live Courses
          </p>
          <p className="text-2xl font-black text-orange-500">
            {courses.length}
          </p>
        </div>

        <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs uppercase font-bold">
            Online Now
          </p>
          <p className="text-2xl font-black text-green-500">
            {onlineStudentsFromUsers.length}
          </p>
        </div>

        <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs uppercase font-bold">
            Lifetime Opens
          </p>
          <p className="text-2xl font-black text-blue-400">
            {accurateLifetimeVideoOpens}
          </p>
        </div>

        <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs uppercase font-bold">
            Opens Today
          </p>
          <p className="text-2xl font-black text-red-400">
            {accurateVideoOpensToday}
          </p>
        </div>

        <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
          <p className="text-gray-400 text-xs uppercase font-bold">
            Opens This Week
          </p>
          <p className="text-2xl font-black text-purple-400">
            {accurateVideoOpensThisWeek}
          </p>
        </div>
      </div>

      {videoOpenStatsError && (
        <div className="mb-6 rounded-lg border border-yellow-500/40 bg-yellow-900/20 p-3 text-xs text-yellow-200">
          Video-open totals could not be refreshed. The dashboard is temporarily showing loaded-log fallback values.
        </div>
      )}

      {alertMessage && (
        <div className="mb-6 bg-blue-900/50 border border-blue-500 text-blue-200 p-4 rounded-lg animate-pulse">
          {alertMessage}
        </div>
      )}

      <section className="mb-10 bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-700">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Manage Students</h2>
            <p className="text-xs text-gray-400 mt-1">
              Add students without expanding a long form inside the mobile page.
            </p>
          </div>

          <button
            onClick={() => setShowRegisterForm(true)}
            className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-full font-semibold transition w-full md:w-auto"
          >
            + Add New Student
          </button>
        </div>
      </section>
      {showRegisterForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl p-5 md:p-7">
            <div className="flex items-center justify-between gap-3 mb-5 border-b border-gray-700 pb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-black text-white">Add New Student</h2>
                <p className="text-xs text-gray-400">Create a student account.</p>
              </div>

              <button
                onClick={() => setShowRegisterForm(false)}
                className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm font-black transition"
              >
                ✕ Close
              </button>
            </div>

            <AddStudentForm
              onStudentAdded={() => {
                setAlertMessage("Student added!");
                setShowRegisterForm(false);
              }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
        <section className="bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-xl border border-gray-700 overflow-hidden h-[620px] lg:h-[900px] flex flex-col">
          <h2 className="text-xl font-bold mb-4 border-b border-gray-700 pb-2">
            User Directory
          </h2>

          <div className="space-y-3 mb-6">
            <input
              type="text"
              placeholder="Search email..."
              className="w-full bg-gray-900 border border-gray-700 p-2 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <select
              className="w-full bg-gray-900 border border-gray-700 p-2 rounded-lg text-xs text-gray-300 outline-none"
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
            >
              <option value="all">Filter by Course: All</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <select
              className="w-full bg-gray-900 border border-gray-700 p-2 rounded-lg text-xs text-gray-300 outline-none"
              value={studentSort}
              onChange={(e) => setStudentSort(e.target.value)}
            >
              <option value="newest">Sort: Newest First</option>
              <option value="oldest">Sort: Oldest First</option>
              <option value="online">Sort: Online First</option>
              <option value="offline">Sort: Offline First</option>
              <option value="videoOpens">Sort: Most Video Opens</option>
              <option value="watchTime">Sort: Most Watch Time</option>
              <option value="az">Sort: A-Z Email</option>
              <option value="za">Sort: Z-A Email</option>
            </select>
          </div>

          <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar flex-1 min-h-0">
            {filteredUsers.length > 0 ? (
              visibleFilteredUsers.map((user) => {
                const online = isStudentOnline(user);
                const abuseLevel = user.abuseLevel || "normal";
                const studentStats = getStudentVideoStats(
                  user.id,
                  user.videoOpenCount || 0
                );
                const newStudent = isNewStudent(user);

                return (
                  <div
                    key={user.id}
                    className="p-4 rounded-xl bg-gray-900/60 border border-gray-700 hover:border-gray-500 transition-all"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-bold text-gray-100 truncate w-full leading-tight">
                            {user.email}
                          </p>

                          {newStudent && (
                            <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-lime-500/20 text-lime-300 border border-lime-500/40 text-[9px] font-black">
                              NEW
                            </span>
                          )}
                        </div>

                        <p className="text-[10px] text-cyan-300/90 font-medium mt-1">
                          Added: {formatCreatedDate(user)}
                        </p>

                        <p className="text-[10px] text-orange-400/80 font-medium">
                          Last login: {formatLastLogin(user.lastLogin)}
                        </p>

                        <p
                          className={`text-[10px] font-black mt-1 ${
                            online ? "text-green-400" : "text-gray-500"
                          }`}
                        >
                          {online ? "🟢 Online" : "⚫ Offline"} | Last seen:{" "}
                          {formatFirestoreTime(user.lastSeen)}
                        </p>
                      </div>

                      <span
                        className={`px-2 py-0.5 rounded text-[10px] uppercase font-black ${
                          user.enabled
                            ? "bg-green-500/20 text-green-400 border border-green-500/50"
                            : "bg-red-500/20 text-red-400 border border-red-500/50"
                        }`}
                      >
                        {user.enabled ? "Active" : "Paused"}
                      </span>
                    </div>

                    <div className="mb-4">
                      <p className="text-[10px] text-purple-300 font-bold mb-1">
                        🆔 UID:
                      </p>

                      <p className="text-[9px] text-gray-500 font-mono break-all mb-2">
                        {user.id}
                      </p>

                      <div className="mb-3">
                        <p className="text-[10px] text-gray-400 font-bold mb-1">
                          💳 Current Plan:
                        </p>

                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black ${
                            getStudentPlanDisplay(user).className
                          }`}
                        >
                          {getStudentPlanDisplay(user).label}
                        </span>
                      </div>

                      <div className="border-t border-gray-700 my-3"></div>

                      <div className="flex flex-wrap items-center gap-4 text-[11px]">
                        <button
                          type="button"
                          onClick={() => copyStudentField(user.email, "Email")}
                          className="text-gray-200 hover:text-cyan-300 transition font-semibold"
                        >
                          📋 Copy Email
                        </button>

                        <button
                          type="button"
                          onClick={() => copyStudentField(user.id, "UID")}
                          className="text-gray-200 hover:text-cyan-300 transition font-semibold"
                        >
                          🆔 Copy UID
                        </button>

                        <button
                          type="button"
                          onClick={() => emailStudent(user.email)}
                          className="text-gray-200 hover:text-cyan-300 transition font-semibold"
                        >
                          ✉️ Email
                        </button>
                      </div>
                    </div>

                    <div className="bg-black/30 p-3 rounded-lg border border-gray-700 mb-4 space-y-1">
                      <p className="text-[11px] text-blue-300 font-bold">
                        📈 Lifetime: {studentStats.lifetime}
                      </p>

                      <p className="text-[11px] text-cyan-300 font-bold">
                        ⏱ Watch Time: {formatDuration(getStudentTotalWatchSeconds(user.id, user.email))}
                      </p>

                      <p className="text-[11px] text-red-300 font-bold">
                        🔥 Today: {studentStats.today}
                      </p>

                      <p className="text-[11px] text-purple-300 font-bold">
                        📅 This Week: {studentStats.week}
                      </p>

                      <p className="text-[11px] text-gray-300">
                        🌍 Country: {user.country || "Unknown"}
                      </p>

                      <p className="text-[11px] text-gray-300 break-all">
                        IP: {user.ip || "Unknown"}
                      </p>

                      <p className="text-[11px] text-gray-300 break-all">
                        🎥 Current video:{" "}
                        {user.currentVideo
                          ? getVideoInfo(user.currentVideo).lesson
                          : "None"}
                      </p>

                      <p className="text-[11px] text-blue-300">
                        📚 Course:{" "}
                        {user.currentVideo
                          ? getVideoInfo(user.currentVideo).course
                          : "None"}
                      </p>

                      <p
                        className={`text-[11px] font-black ${
                          abuseLevel === "warning"
                            ? "text-red-400"
                            : "text-green-400"
                        }`}
                      >
                        🚨 Abuse status: {abuseLevel}
                      </p>
                    </div>

                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() =>
                          handleToggleStatus(user.id, user.enabled)
                        }
                        className="flex-1 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-xs font-bold transition"
                      >
                        {user.enabled ? "Disable" : "Enable"}
                      </button>

                      <button
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        className="flex-1 py-1.5 rounded-md bg-red-900/30 border border-red-700/50 hover:bg-red-600 text-xs font-bold transition text-red-200"
                      >
                        Delete
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => openEditStudentAccount(user)}
                      className="w-full mb-2 py-2 rounded-md bg-purple-900/30 border border-purple-600/50 hover:bg-purple-700 text-xs font-black transition text-purple-200"
                    >
                      ✏️ Edit Email, Password & Plan
                    </button>

                    <button
                      onClick={() => openStudentHistory(user)}
                      className="w-full mb-4 py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                    >
                      👤 View Full Watch History
                    </button>

                    <div className="flex justify-between items-center text-xs bg-indigo-900/20 p-2 rounded border border-indigo-500/30 mb-4">
                      <span className="text-indigo-300 font-bold flex items-center gap-1">
                        🤖 AI Interview
                      </span>

                      <button
                        onClick={() =>
                          handleToggleAiAccess(
                            user.id,
                            user.hasAiAccess || false
                          )
                        }
                        className={`font-black uppercase text-[10px] px-2 py-1 rounded transition-colors ${
                          user.hasAiAccess
                            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        }`}
                      >
                        {user.hasAiAccess ? "Revoke Access" : "Grant Access"}
                      </button>
                    </div>

                    <p className="text-[10px] font-bold text-gray-500 mb-2 uppercase tracking-widest">
                      Course Control
                    </p>

                    <div className="space-y-1">
                      {courses.map((course) => {
                        const hasAccess = user.authorizedCourses?.includes(
                          course.id
                        );

                        return (
                          <div
                            key={course.id}
                            className="flex justify-between items-center text-xs bg-black/20 p-2 rounded"
                          >
                            <span className="text-gray-300 truncate mr-2">
                              {course.name}
                            </span>

                            <button
                              onClick={() =>
                                handleToggleAccess(
                                  user.id,
                                  course.id,
                                  hasAccess
                                )
                              }
                              className={`font-bold transition-colors flex-shrink-0 ${
                                hasAccess
                                  ? "text-red-400 hover:text-red-300"
                                  : "text-green-400 hover:text-green-300"
                              }`}
                            >
                              {hasAccess ? "Revoke" : "Grant"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-10 text-gray-500 italic text-sm">
                No results found.
              </div>
            )}

            {hasMoreStudents && (
              <button
                type="button"
                onClick={() => setVisibleStudentCount((count) => count + 50)}
                className="w-full mt-4 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 text-sm font-black transition"
              >
                Load More Students ({visibleFilteredUsers.length} of {filteredUsers.length})
              </button>
            )}
          </div>
        </section>

      <section className="lg:col-span-2 bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-xl border border-gray-700 h-[620px] lg:h-[900px] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-green-400">
              🟢 Who Is Online Now
            </h2>
            <p className="text-gray-400 text-xs">
              Auto offline after 45 seconds of inactivity.
            </p>
          </div>

          <span className="text-xs bg-green-500/20 text-green-300 border border-green-500/40 px-3 py-1 rounded-full font-bold">
            {onlineStudentsFromUsers.length} online
          </span>
        </div>

        {onlineStudentsFromUsers.length === 0 ? (
          <p className="text-gray-500 italic">Nobody is online.</p>
        ) : (
          <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar flex-1 min-h-0">
            {onlineStudentsFromUsers.map((student) => (
              <div
                key={student.id || student.email}
                className="bg-gray-900 border border-gray-700 rounded-xl p-4"
              >
                <p className="text-green-400 font-bold">
                  {student.email || "Unknown student"}
                </p>

                <p className="text-orange-400 text-sm mt-1">
                  🎥{" "}
                  {student.currentVideo
                    ? getVideoInfo(student.currentVideo).lesson
                    : "Browsing course"}
                </p>

                <p className="text-xs text-blue-300 mt-1">
                  📚{" "}
                  {student.currentVideo
                    ? getVideoInfo(student.currentVideo).course
                    : "No course"}
                </p>

                <p className="text-xs text-gray-400 mt-1 break-all">
                  Current path: {student.currentVideo || "None"}
                </p>

                <p className="text-xs text-gray-500 mt-1">
                  🌍 {student.country || "Unknown"} | IP:{" "}
                  {student.ip || "Unknown"}
                </p>

                <p className="text-[10px] text-gray-600 mt-1">
                  Last seen: {formatFirestoreTime(student.lastSeen)}
                </p>

                <button
                  onClick={() => openStudentHistory(student)}
                  className="mt-3 w-full py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                >
                  👤 View Full Watch History
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      </div>



      <section className="mb-10 bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-2xl font-bold text-cyan-400">📊 Analytics Center</h2>
            <p className="text-gray-400 text-xs">
              Open analytics as dedicated internal pages while keeping everything inside AdminDashboard.jsx.
            </p>
          </div>
          <span className="text-xs bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 px-3 py-1 rounded-full font-bold w-fit">
            Dashboard modules
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          <button
            onClick={() => {
              setActiveAnalyticsPage("analyticsCenter");
              setAdminView("analytics");
            }}
            className="text-left bg-gray-900/80 border border-cyan-700/50 hover:border-cyan-400 rounded-xl p-5 transition"
          >
            <p className="text-2xl mb-2">📈</p>
            <h3 className="font-black text-cyan-300">Analytics Center</h3>
            <p className="text-xs text-gray-400 mt-1">Date filters, 24-hour breakdown, Bunny comparison, alerts, and bandwidth intelligence.</p>
            <p className="text-xs text-cyan-400 font-black mt-3">Open →</p>
          </button>

          <button
            onClick={() => {
              setActiveAnalyticsPage("watchCost");
              setAdminView("analytics");
            }}
            className="text-left bg-gray-900/80 border border-emerald-700/50 hover:border-emerald-400 rounded-xl p-5 transition"
          >
            <p className="text-2xl mb-2">📊</p>
            <h3 className="font-black text-emerald-300">Watch Analytics & Bunny Cost</h3>
            <p className="text-xs text-gray-400 mt-1">Total watch time, sessions, estimated bandwidth, and estimated Bunny cost.</p>
            <p className="text-xs text-emerald-400 font-black mt-3">Open →</p>
          </button>

          <button
            onClick={() => {
              setActiveAnalyticsPage("lifetimeOpens");
              setAdminView("analytics");
            }}
            className="text-left bg-gray-900/80 border border-purple-700/50 hover:border-purple-400 rounded-xl p-5 transition"
          >
            <p className="text-2xl mb-2">📈</p>
            <h3 className="font-black text-purple-300">Lifetime Video Opens</h3>
            <p className="text-xs text-gray-400 mt-1">Top students and lifetime video activity totals.</p>
            <p className="text-xs text-purple-400 font-black mt-3">Open →</p>
          </button>

          <button
            onClick={() => {
              setActiveAnalyticsPage("recentActivity");
              setAdminView("analytics");
            }}
            className="text-left bg-gray-900/80 border border-orange-700/50 hover:border-orange-400 rounded-xl p-5 transition"
          >
            <p className="text-2xl mb-2">🎥</p>
            <h3 className="font-black text-orange-300">Recent Video Activity</h3>
            <p className="text-xs text-gray-400 mt-1">Latest video opens with student, course, path, IP, country, and timestamp.</p>
            <p className="text-xs text-orange-400 font-black mt-3">Open →</p>
          </button>


          <button
            onClick={() => {
              setActiveAnalyticsPage("loginWatchHistory");
              setAdminView("analytics");
            }}
            className="text-left bg-gray-900/80 border border-blue-700/50 hover:border-blue-400 rounded-xl p-5 transition"
          >
            <p className="text-2xl mb-2">👥</p>
            <h3 className="font-black text-blue-300">Login & Watch History</h3>
            <p className="text-xs text-gray-400 mt-1">See who logged in, who watched, video opens, watch time, estimated traffic, and cost.</p>
            <p className="text-xs text-blue-400 font-black mt-3">Open →</p>
          </button>
        </div>
      </section>
        </>
      )}


      {adminView === "analytics" && (
        <>
          <section className="mb-6 bg-gray-800 p-4 rounded-2xl shadow-xl border border-gray-700">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setActiveAnalyticsPage("analyticsCenter")} className={`px-4 py-2 rounded-lg text-xs font-black border transition ${activeAnalyticsPage === "analyticsCenter" ? "bg-cyan-600 border-cyan-400 text-white" : "bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700"}`}>📈 Analytics Center</button>
              <button onClick={() => setActiveAnalyticsPage("watchCost")} className={`px-4 py-2 rounded-lg text-xs font-black border transition ${activeAnalyticsPage === "watchCost" ? "bg-emerald-600 border-emerald-400 text-white" : "bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700"}`}>📊 Watch Analytics & Bunny Cost</button>
              <button onClick={() => setActiveAnalyticsPage("lifetimeOpens")} className={`px-4 py-2 rounded-lg text-xs font-black border transition ${activeAnalyticsPage === "lifetimeOpens" ? "bg-purple-600 border-purple-400 text-white" : "bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700"}`}>📈 Lifetime Video Opens</button>
              <button onClick={() => setActiveAnalyticsPage("recentActivity")} className={`px-4 py-2 rounded-lg text-xs font-black border transition ${activeAnalyticsPage === "recentActivity" ? "bg-orange-600 border-orange-400 text-white" : "bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700"}`}>🎥 Recent Video Activity</button>
              <button onClick={() => setActiveAnalyticsPage("loginWatchHistory")} className={`px-4 py-2 rounded-lg text-xs font-black border transition ${activeAnalyticsPage === "loginWatchHistory" ? "bg-blue-600 border-blue-400 text-white" : "bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700"}`}>👥 Login & Watch History</button>
            </div>
            <p className="text-[11px] text-gray-500 mt-3">
              Each button now opens its own dedicated analytics view inside this same AdminDashboard.jsx file.
            </p>
          </section>

      {activeAnalyticsPage === "analyticsCenter" && (
        <>
      <section className="mb-10 bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-700">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-5">
          <div>
            <h2 className="text-2xl font-bold text-cyan-400">
              📈 Analytics Center
            </h2>
            <p className="text-gray-400 text-xs">
              Watch time, students, sessions, estimated Bunny traffic, and estimated Bunny cost.
            </p>
          </div>

          <span className="text-xs bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 px-3 py-1 rounded-full font-bold w-fit">
            {getAnalyticsRangeLabel(analyticsRange)} Report
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {analyticsRangeOptions.map((option) => (
            <button
              key={option.key}
              onClick={() => {
                setAnalyticsRange(option.key);
                setSelectedAnalyticsDay(null);
                setExpandedHour(null);
                setExpandedHourStudent(null);
              }}
              className={`px-4 py-2 rounded-lg text-xs font-black border transition ${
                analyticsRange === option.key
                  ? "bg-cyan-600 border-cyan-400 text-white"
                  : "bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {analyticsDuplicateSessions > 0 && (
          <div className="mb-6 rounded-xl border border-yellow-500/60 bg-yellow-900/20 p-4">
            <h3 className="font-black text-yellow-300">
              ⚠ Duplicate Sessions Detected
            </h3>
            <p className="text-sm text-yellow-100 mt-2">
              {analyticsDuplicateSessions} duplicate session
              {analyticsDuplicateSessions !== 1 ? "s were" : " was"} automatically ignored
              in this report so watch time, completion, traffic, and Bunny cost stay accurate.
            </p>
            <p className="text-[11px] text-yellow-200/70 mt-2">
              Total duplicates found in all loaded sessions: {totalDuplicateSessions}.
            </p>
          </div>
        )}

        <div className="mb-6 bg-gray-900/80 border border-emerald-700/50 rounded-2xl p-5">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-xl font-black text-emerald-400">
                🐰 Real Bunny Analytics
              </h3>
              <p className="text-xs text-gray-400">
                Actual Bunny CDN traffic from Bunny API, cached for 10 minutes.
              </p>
            </div>

            <button
              onClick={() => setBunnyRefreshNonce((value) => value + 1)}
              disabled={bunnyLoading}
              className={`px-4 py-2 rounded-lg text-xs font-black border transition w-fit ${
                bunnyLoading
                  ? "bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
                  : "bg-emerald-900/40 border-emerald-600/60 text-emerald-200 hover:bg-emerald-700"
              }`}
            >
              {bunnyLoading ? "Loading Bunny..." : "🔄 Refresh Bunny Data"}
            </button>
          </div>

          {bunnyError ? (
            <div className="mb-4 bg-red-900/30 border border-red-600/50 text-red-200 p-3 rounded-lg text-sm">
              Bunny API error: {bunnyError}
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 mb-4">
            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase font-bold">
                Actual Bandwidth
              </p>
              <p className="text-2xl font-black text-emerald-300">
                {bunnyLoading && !bunnyAnalytics ? "Loading..." : formatGB(bunnyActualGB)}
              </p>
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase font-bold">
                Cost From Real Traffic
              </p>
              <p className="text-2xl font-black text-green-400">
                {bunnyLoading && !bunnyAnalytics ? "Loading..." : formatMoney(bunnyActualCost)}
              </p>
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase font-bold">
                Requests Served
              </p>
              <p className="text-2xl font-black text-orange-300">
                {bunnyLoading && !bunnyAnalytics ? "Loading..." : formatNumber(bunnyRequests)}
              </p>
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase font-bold">
                Origin Traffic
              </p>
              <p className="text-2xl font-black text-purple-300">
                {bunnyLoading && !bunnyAnalytics ? "Loading..." : formatGB(bunnyOriginGB)}
              </p>
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase font-bold">
                Cache Hit Ratio
              </p>
              <p className="text-2xl font-black text-blue-300">
                {bunnyLoading && !bunnyAnalytics ? "Loading..." : `${bunnyCacheHitRate.toFixed(2)}%`}
              </p>
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase font-bold">
                Last Bunny Sync
              </p>
              <p className="text-sm font-black text-white">
                {formatBunnySyncTime(bunnyAnalytics?.lastSyncAt || bunnyAnalytics?.lastSyncMs)}
              </p>
              <p className="text-[10px] text-gray-500">
                {bunnyAnalytics?.fromCache ? "From 10-min cache" : "Fresh API data"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-xs text-gray-400 uppercase font-bold">
                App Estimate vs Bunny Actual
              </p>
              <p className="text-sm text-gray-300 mt-2">
                App estimated traffic: <span className="text-purple-300 font-bold">{formatGB(analyticsEstimatedGB)}</span>
              </p>
              <p className="text-sm text-gray-300">
                Bunny actual traffic: <span className="text-emerald-300 font-bold">{formatGB(bunnyActualGB)}</span>
              </p>
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-xs text-gray-400 uppercase font-bold">
                Traffic Difference
              </p>
              <p className={`text-2xl font-black ${bunnyDifferenceGB >= 0 ? "text-orange-300" : "text-blue-300"}`}>
                {bunnyDifferenceGB >= 0 ? "+" : ""}{bunnyDifferenceGB.toFixed(4)} GB
              </p>
              <p className="text-[10px] text-gray-500">
                Positive means Bunny reported more traffic than the app estimate.
              </p>
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <p className="text-xs text-gray-400 uppercase font-bold">
                Cost Difference
              </p>
              <p className={`text-2xl font-black ${bunnyDifferenceCost >= 0 ? "text-orange-300" : "text-blue-300"}`}>
                {bunnyDifferenceCost >= 0 ? "+" : ""}{formatMoney(bunnyDifferenceCost)}
              </p>
              <p className="text-[10px] text-gray-500">
                Bunny cost is calculated from real Bunny bandwidth × $0.03/GB.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 bg-gray-900/80 border border-red-700/50 rounded-2xl p-5">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-xl font-black text-red-400">
                🚨 Bandwidth Alerts
              </h3>
              <p className="text-xs text-gray-400">
                Automatic alerts for unusual bandwidth, high-cost sessions, and possible abuse in the selected report range.
              </p>
            </div>

            <span className={`text-xs px-3 py-1 rounded-full font-bold border w-fit ${
              highRiskAlertCount > 0
                ? "bg-red-500/20 text-red-300 border-red-500/50"
                : bandwidthAlerts.length > 0
                ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/50"
                : "bg-green-500/20 text-green-300 border-green-500/50"
            }`}>
              {highRiskAlertCount > 0
                ? `${highRiskAlertCount} high-risk alert${highRiskAlertCount !== 1 ? "s" : ""}`
                : bandwidthAlerts.length > 0
                ? `${bandwidthAlerts.length} watch alert${bandwidthAlerts.length !== 1 ? "s" : ""}`
                : "No alerts"}
            </span>
          </div>

          {bandwidthAlerts.length === 0 ? (
            <div className="bg-black/30 border border-green-700/40 rounded-xl p-4">
              <p className="text-green-300 font-black">✅ No unusual bandwidth patterns detected.</p>
              <p className="text-xs text-gray-400 mt-1">
                No student, video, or session crossed the alert thresholds for {getAnalyticsRangeLabel(analyticsRange)}.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {bandwidthAlerts.map((alert) => (
                <div
                  key={alert.key}
                  className={`rounded-xl p-4 border ${
                    alert.severity === "high"
                      ? "bg-red-950/30 border-red-600/60"
                      : "bg-yellow-950/20 border-yellow-600/50"
                  }`}
                >
                  <div className="flex justify-between gap-3">
                    <div className="overflow-hidden">
                      <p className={`text-xs font-black uppercase ${
                        alert.severity === "high" ? "text-red-300" : "text-yellow-300"
                      }`}>
                        {alert.severity === "high" ? "High Risk" : "Watch"} • {alert.subtitle}
                      </p>
                      <p className="text-sm font-black text-white mt-1 truncate">
                        {alert.icon} {alert.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {alert.detail}
                      </p>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className={`text-xl font-black ${
                        alert.severity === "high" ? "text-red-300" : "text-yellow-300"
                      }`}>
                        {alert.metric}
                      </p>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-300 mt-3">
                    💡 {alert.recommendation}
                  </p>

                  {alert.action && (
                    <button
                      onClick={alert.action}
                      className="mt-3 w-full py-2 rounded-lg bg-red-900/30 border border-red-600/50 hover:bg-red-700 text-xs font-black text-red-100 transition"
                    >
                      {alert.actionLabel || "View Details"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-6 bg-gray-900/80 border border-orange-700/50 rounded-2xl p-5">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-xl font-black text-orange-400">
                🔥 Bandwidth Intelligence Center
              </h3>
              <p className="text-xs text-gray-400">
                Shows where estimated bandwidth and Bunny cost are coming from for the selected report range.
              </p>
            </div>

            <span className="text-xs bg-orange-500/20 text-orange-300 border border-orange-500/40 px-3 py-1 rounded-full font-bold w-fit">
              {getAnalyticsRangeLabel(analyticsRange)} bandwidth view
            </span>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <h4 className="text-lg font-bold text-orange-300 mb-3">
                🎥 Top Bandwidth Videos
              </h4>

              {analyticsTopBandwidthVideos.length === 0 ? (
                <p className="text-gray-500 italic text-sm">No video bandwidth data for this range.</p>
              ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
                  {analyticsTopBandwidthVideos.map((video, index) => (
                    <div
                      key={video.videoKey}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedBandwidthVideo(video)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedBandwidthVideo(video);
                        }
                      }}
                      className="bg-gray-900/80 border border-gray-700 hover:border-orange-500 rounded-lg p-3 cursor-pointer transition"
                    >
                      <div className="flex justify-between gap-3">
                        <div className="overflow-hidden">
                          <p className="text-sm font-black text-white truncate">
                            #{index + 1} {video.lessonTitle}
                          </p>
                          <p className="text-[10px] text-blue-300 mt-1 truncate">
                            📚 {video.course}
                          </p>
                          <p className="text-[10px] text-gray-500 mt-1 break-all">
                            {video.videoPath || "No path saved"}
                          </p>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-black text-purple-300">
                            {formatGB(video.estimatedGB)}
                          </p>
                          <p className="text-xs text-green-400 font-bold">
                            {formatMoney(video.estimatedCost)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[11px]">
                        <p className="text-cyan-300 font-bold">
                          ⏱ {formatDuration(video.watchSeconds)}
                        </p>
                        <p className="text-orange-300 font-bold">
                          🎥 {video.sessions} sessions
                        </p>
                        <p className="text-green-300 font-bold">
                          👥 {video.studentCount} students
                        </p>
                        <p className="text-purple-300 font-bold">
                          ✅ {video.averageCompletion}% avg
                        </p>
                      </div>

                      <p className="text-[10px] text-orange-200 mt-3 font-bold">
                        Click to see every student who watched this video →
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <h4 className="text-lg font-bold text-cyan-300 mb-3">
                👥 Top Students by Bandwidth
              </h4>

              {analyticsTopBandwidthStudents.length === 0 ? (
                <p className="text-gray-500 italic text-sm">No student bandwidth data for this range.</p>
              ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
                  {analyticsTopBandwidthStudents.map((student, index) => (
                    <div
                      key={student.uid || student.email}
                      className="w-full text-left bg-gray-900/80 border border-gray-700 hover:border-cyan-500 rounded-lg p-3 transition"
                    >
                      <div className="flex justify-between gap-3">
                        <div className="overflow-hidden">
                          <p className="text-sm font-black text-white truncate">
                            #{index + 1} {student.email}
                          </p>
                          <p className="text-xs text-cyan-300 mt-1">
                            ⏱ {formatDuration(student.watchSeconds)} watched
                          </p>
                          <p className="text-[10px] text-gray-500 mt-1">
                            {student.videoCount} videos • {student.sessions} sessions • {student.averageCompletion}% avg completion
                          </p>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-black text-purple-300">
                            {formatGB(student.estimatedGB)}
                          </p>
                          <p className="text-xs text-green-400 font-bold">
                            {formatMoney(student.estimatedCost)}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => openStudentHistory(student)}
                          className="py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                        >
                          👤 Full Watch History
                        </button>

                        <button
                          type="button"
                          onClick={() => setSelectedBandwidthStudent(student)}
                          className="py-2 rounded-md bg-cyan-900/30 border border-cyan-600/50 hover:bg-cyan-700 text-xs font-black transition text-cyan-200"
                        >
                          📊 Bandwidth Profile
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <h4 className="text-lg font-bold text-blue-300 mb-3">
                📚 Top Courses by Bandwidth
              </h4>

              {analyticsTopBandwidthCourses.length === 0 ? (
                <p className="text-gray-500 italic text-sm">No course bandwidth data for this range.</p>
              ) : (
                <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2 custom-scrollbar">
                  {analyticsTopBandwidthCourses.map((course, index) => (
                    <div
                      key={course.course}
                      className="bg-gray-900/80 border border-gray-700 rounded-lg p-3"
                    >
                      <div className="flex justify-between gap-3">
                        <div className="overflow-hidden">
                          <p className="text-sm font-black text-white truncate">
                            #{index + 1} {course.course}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {course.studentCount} students • {course.sessions} sessions
                          </p>
                          <p className="text-xs text-cyan-300 mt-1">
                            Watch time: {formatDuration(course.watchSeconds)}
                          </p>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-black text-purple-300">
                            {formatGB(course.estimatedGB)}
                          </p>
                          <p className="text-xs text-green-400 font-bold">
                            {formatMoney(course.estimatedCost)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-black/30 border border-gray-700 rounded-xl p-4">
              <h4 className="text-lg font-bold text-red-300 mb-3">
                🚨 Highest Cost Sessions
              </h4>

              {analyticsHighestCostSessions.length === 0 ? (
                <p className="text-gray-500 italic text-sm">No high-cost sessions for this range.</p>
              ) : (
                <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2 custom-scrollbar">
                  {analyticsHighestCostSessions.map((session, index) => {
                    const videoInfo = getVideoInfo(session.videoPath);

                    return (
                      <div
                        key={session.id || session.sessionId || `${session.email}-${index}`}
                        className="bg-gray-900/80 border border-gray-700 rounded-lg p-3"
                      >
                        <div className="flex justify-between gap-3">
                          <div className="overflow-hidden">
                            <p className="text-sm font-black text-white truncate">
                              #{index + 1} {session.email || "Unknown student"}
                            </p>
                            <p className="text-xs text-orange-300 mt-1 truncate">
                              🎥 {session.lessonTitle || videoInfo.lesson}
                            </p>
                            <p className="text-[10px] text-blue-300 mt-1 truncate">
                              📚 {session.courseId || videoInfo.course}
                            </p>
                            <p className="text-[10px] text-gray-500 mt-1">
                              {formatSessionClockTime(session.startedAtMs)} - {formatSessionClockTime(session.endedAtMs)}
                            </p>
                          </div>

                          <div className="text-right flex-shrink-0">
                            <p className="text-lg font-black text-purple-300">
                              {formatGB(session.estimatedGB)}
                            </p>
                            <p className="text-xs text-green-400 font-bold">
                              {formatMoney(session.estimatedCost)}
                            </p>
                            <p className="text-[10px] text-gray-500 mt-1">
                              {session.completionPercent || 0}% • {formatDuration(session.watchSeconds)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-4 mb-6">
          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Total Watch Time
            </p>
            <p className="text-2xl font-black text-white">
              {formatDuration(analyticsWatchSeconds)}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Active Students
            </p>
            <p className="text-2xl font-black text-green-400">
              {analyticsActiveStudents}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Sessions
            </p>
            <p className="text-2xl font-black text-orange-400">
              {analyticsSessions.length}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Avg / Student
            </p>
            <p className="text-2xl font-black text-blue-400">
              {formatDuration(analyticsAverageWatchPerStudent)}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Avg Completion
            </p>
            <p className="text-2xl font-black text-purple-400">
              {analyticsAverageCompletion}%
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Est. Traffic
            </p>
            <p className="text-2xl font-black text-purple-300">
              {analyticsEstimatedGB.toFixed(4)} GB
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Est. Cost
            </p>
            <p className="text-2xl font-black text-green-400">
              {formatMoney(analyticsEstimatedCost)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
          <div className="bg-gray-900/70 border border-gray-700 rounded-xl p-4">
            <h3 className="text-lg font-bold text-cyan-400 mb-3">
              🔥 Peak Hour
            </h3>
            {peakAnalyticsHour && peakAnalyticsHour.watchSeconds > 0 ? (
              <div>
                <p className="text-2xl font-black text-white">
                  {peakAnalyticsHour.label}
                </p>
                <p className="text-sm text-cyan-300 mt-1">
                  {formatDuration(peakAnalyticsHour.watchSeconds)} watched
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {peakAnalyticsHour.studentCount} students • {peakAnalyticsHour.sessions} sessions
                </p>
              </div>
            ) : (
              <p className="text-gray-500 italic">No watch time for this range yet.</p>
            )}
          </div>

          <div className="xl:col-span-2 bg-gray-900/70 border border-gray-700 rounded-xl p-4">
            <h3 className="text-lg font-bold text-orange-400 mb-3">
              🏆 Top Watch Hours
            </h3>
            {topAnalyticsHours.length === 0 ? (
              <p className="text-gray-500 italic">No top hours yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {topAnalyticsHours.map((hour, index) => (
                  <div
                    key={hour.hour}
                    className="bg-black/30 border border-gray-700 rounded-lg p-3"
                  >
                    <p className="text-xs text-gray-400 font-bold">
                      #{index + 1}
                    </p>
                    <p className="text-lg font-black text-white">
                      {hour.label}
                    </p>
                    <p className="text-sm text-cyan-300">
                      {formatDuration(hour.watchSeconds)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {hour.studentCount} students • {hour.sessions} sessions
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {shouldShowDayPicker && !selectedAnalyticsDay && (
          <div className="mb-6 bg-gray-900/70 border border-gray-700 rounded-xl p-4">
            <h3 className="text-lg font-black text-cyan-300 mb-3">
              📅 Choose a Day to View 24-Hour Breakdown
            </h3>

            {analyticsDayRows.length === 0 ? (
              <p className="text-gray-500 italic text-sm">No analytics data found.</p>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                {analyticsDayRows.map((day) => (
                  <button
                    key={day.dayKey}
                    type="button"
                    onClick={() => {
                      setSelectedAnalyticsDay(day.dayKey);
                      setExpandedHour(null);
                      setExpandedHourStudent(null);
                    }}
                    className="w-full bg-black/30 border border-gray-700 hover:border-cyan-500 rounded-xl p-4 text-left transition"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <p className="text-white font-black">
                          📅 {formatDayLabel(day.dayKey)}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Click to view the 24-hour breakdown for this day.
                        </p>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                        <p className="text-cyan-300 font-bold">
                          ⏱ {formatDuration(day.watchSeconds)}
                        </p>
                        <p className="text-green-300 font-bold">
                          👥 {day.studentCount}
                        </p>
                        <p className="text-orange-300 font-bold">
                          🎥 {day.sessions}
                        </p>
                        <p className="text-purple-300 font-bold">
                          🌐 {day.estimatedGB.toFixed(4)} GB
                        </p>
                        <p className="text-green-400 font-black">
                          💲 {formatMoney(day.estimatedCost)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
          {(!shouldShowDayPicker || selectedAnalyticsDay) && (
          <div className="bg-gray-900/70 border border-gray-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-black/30">
              <h3 className="text-lg font-bold text-cyan-400">
                🕒 24-Hour Breakdown
              </h3>
              <p className="text-[11px] text-gray-500">
                Grouped by session start time.
              </p>
            </div>

            {selectedAnalyticsDay && (
              <div className="m-4 mb-0 flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-cyan-900/20 border border-cyan-600/40 rounded-xl p-4">
                <div>
                  <p className="text-cyan-300 text-xs font-bold uppercase">
                    Viewing Day
                  </p>
                  <p className="text-white font-black">
                    {formatDayLabel(selectedAnalyticsDay)}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setSelectedAnalyticsDay(null);
                    setExpandedHour(null);
                    setExpandedHourStudent(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-gray-900 border border-gray-700 hover:bg-gray-700 text-xs font-black w-fit"
                >
                  ← Back to Days
                </button>
              </div>
            )}

            <div className="grid grid-cols-6 gap-2 px-4 py-3 bg-black/20 text-[10px] text-gray-400 uppercase font-black">
              <p>Hour</p>
              <p className="text-right">Watch</p>
              <p className="text-right">Students</p>
              <p className="text-right">Sessions</p>
              <p className="text-right">GB</p>
              <p className="text-right">Cost</p>
            </div>

            <div className="max-h-[520px] overflow-y-auto custom-scrollbar">
              {hourlyWatchRows.map((row) => {
                const widthPercent = Math.max(
                  3,
                  Math.round((row.watchSeconds / maxHourlyWatchSeconds) * 100)
                );

                const isExpanded = expandedHour === row.hour;
                const hourStudents = getStudentsForHour(row.hour);

                return (
                  <div key={row.hour} className="border-t border-gray-800">
                    <button
                      type="button"
                      onClick={() => {
                          setExpandedHour(isExpanded ? null : row.hour);
                          setExpandedHourStudent(null);
                        }}
                      className="w-full grid grid-cols-6 gap-2 px-4 py-3 hover:bg-gray-800/70 transition text-left"
                    >
                      <div>
                        <p className="text-xs font-bold text-white">
                          {isExpanded ? "▼" : "▶"} {row.label}
                        </p>

                        <div className="mt-2 h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-cyan-500 rounded-full"
                            style={{
                              width: row.watchSeconds > 0 ? `${widthPercent}%` : "0%",
                            }}
                          />
                        </div>
                      </div>

                      <p className="text-xs text-right font-bold text-cyan-300">
                        {formatDuration(row.watchSeconds)}
                      </p>

                      <p className="text-xs text-right text-green-300">
                        {row.studentCount}
                      </p>

                      <p className="text-xs text-right text-orange-300">
                        {row.sessions}
                      </p>

                      <p className="text-xs text-right text-purple-300">
                        {row.estimatedGB.toFixed(4)}
                      </p>

                      <p className="text-xs text-right text-green-400 font-bold">
                        {formatMoney(row.estimatedCost)}
                      </p>
                    </button>

                    {isExpanded && (
                      <div className="bg-black/30 px-4 py-4 border-t border-gray-800">
                        {hourStudents.length === 0 ? (
                          <p className="text-xs text-gray-500 italic">
                            No students active during this hour.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {hourStudents.map((student, index) => {
                              const studentKey = `${row.hour}-${student.uid || student.email}`;
                              const isStudentExpanded = expandedHourStudent === studentKey;
                              const studentSessions = getSessionsForHourStudent(row.hour, student);
                              const timelineEvents = getTimelineEventsForStudent(studentSessions);
                              const copyEmailKey = `${studentKey}-email`;
                              const copyUidKey = `${studentKey}-uid`;

                              return (
                                <div
                                  key={student.uid || student.email}
                                  className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden"
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setExpandedHourStudent(isStudentExpanded ? null : studentKey);
                                    }}
                                    className="w-full p-3 flex justify-between gap-3 text-left hover:bg-gray-800/80 transition"
                                  >
                                    <div className="overflow-hidden">
                                      <p className="text-sm font-bold text-white truncate">
                                        {isStudentExpanded ? "▼" : "▶"}{" "}
                                        {index === 0
                                          ? "🥇"
                                          : index === 1
                                          ? "🥈"
                                          : index === 2
                                          ? "🥉"
                                          : `#${index + 1}`} {" "}
                                        {student.email}
                                      </p>

                                      <p className="text-[11px] text-gray-400">
                                        {student.videosWatched} videos •{" "}
                                        {student.completedVideos} completed •{" "}
                                        {student.incompleteVideos} incomplete
                                      </p>
                                    </div>

                                    <div className="text-right flex-shrink-0">
                                      <p className="text-sm font-black text-cyan-300">
                                        {formatDuration(student.watchSeconds)}
                                      </p>

                                      <p className="text-xs text-purple-300">
                                        {student.estimatedGB.toFixed(4)} GB
                                      </p>

                                      <p className="text-xs text-green-400 font-bold">
                                        {formatMoney(student.estimatedCost)}
                                      </p>
                                    </div>
                                  </button>

                                  {isStudentExpanded && (
                                    <div className="border-t border-gray-700 bg-black/30 p-4">
                                      <div className="mb-4 bg-gray-950/70 border border-gray-700 rounded-lg p-3 space-y-3">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                          <div className="min-w-0">
                                            <p className="text-[10px] text-gray-500 uppercase font-black">
                                              👤 Email
                                            </p>
                                            <p className="text-sm font-bold text-white break-all">
                                              {student.email || "Unknown student"}
                                            </p>
                                          </div>

                                          <div className="flex flex-wrap gap-2 flex-shrink-0">
                                            <button
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                copyActivityText(student.email, copyEmailKey);
                                              }}
                                              className="px-3 py-1 rounded-md bg-blue-900/40 border border-blue-600/50 text-blue-200 hover:bg-blue-700 text-[11px] font-black transition"
                                            >
                                              {copiedActivityField === copyEmailKey ? "✅ Email Copied" : "📋 Copy Email"}
                                            </button>

                                            {student.email && student.email !== "Unknown student" && (
                                              <a
                                                href={`mailto:${student.email}`}
                                                onClick={(event) => event.stopPropagation()}
                                                className="px-3 py-1 rounded-md bg-purple-900/40 border border-purple-600/50 text-purple-200 hover:bg-purple-700 text-[11px] font-black transition"
                                              >
                                                📧 Email
                                              </a>
                                            )}
                                          </div>
                                        </div>

                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border-t border-gray-800 pt-3">
                                          <div className="min-w-0">
                                            <p className="text-[10px] text-gray-500 uppercase font-black">
                                              🆔 UID
                                            </p>
                                            <p className="text-xs text-gray-300 break-all">
                                              {student.uid || "No UID saved"}
                                            </p>
                                          </div>

                                          <button
                                            type="button"
                                            disabled={!student.uid}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              copyActivityText(student.uid, copyUidKey);
                                            }}
                                            className={`px-3 py-1 rounded-md border text-[11px] font-black transition flex-shrink-0 ${
                                              student.uid
                                                ? "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700"
                                                : "bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed"
                                            }`}
                                          >
                                            {copiedActivityField === copyUidKey ? "✅ UID Copied" : "📋 Copy UID"}
                                          </button>
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        <div className="bg-gray-950/70 border border-gray-700 rounded-lg p-3">
                                          <p className="text-[10px] text-gray-500 uppercase font-black">
                                            Videos Opened
                                          </p>
                                          <p className="text-xl font-black text-orange-300">
                                            {student.videosWatched}
                                          </p>
                                        </div>

                                        <div className="bg-gray-950/70 border border-gray-700 rounded-lg p-3">
                                          <p className="text-[10px] text-gray-500 uppercase font-black">
                                            Watch Time
                                          </p>
                                          <p className="text-xl font-black text-cyan-300">
                                            {formatDuration(student.watchSeconds)}
                                          </p>
                                        </div>

                                        <div className="bg-gray-950/70 border border-gray-700 rounded-lg p-3">
                                          <p className="text-[10px] text-gray-500 uppercase font-black">
                                            Avg Completion
                                          </p>
                                          <p className="text-xl font-black text-blue-300">
                                            {student.averageCompletion}%
                                          </p>
                                        </div>

                                        <div className="bg-gray-950/70 border border-gray-700 rounded-lg p-3">
                                          <p className="text-[10px] text-gray-500 uppercase font-black">
                                            Completed
                                          </p>
                                          <p className="text-xl font-black text-green-300">
                                            {student.completedVideos}
                                          </p>
                                        </div>

                                        <div className="bg-gray-950/70 border border-gray-700 rounded-lg p-3">
                                          <p className="text-[10px] text-gray-500 uppercase font-black">
                                            Incomplete
                                          </p>
                                          <p className="text-xl font-black text-red-300">
                                            {student.incompleteVideos}
                                          </p>
                                        </div>

                                        <div className="bg-gray-950/70 border border-gray-700 rounded-lg p-3">
                                          <p className="text-[10px] text-gray-500 uppercase font-black">
                                            Bunny Cost
                                          </p>
                                          <p className="text-xl font-black text-green-400">
                                            {formatMoney(student.estimatedCost)}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="mt-3 bg-gray-950/70 border border-gray-700 rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                        <p className="text-xs text-gray-400">
                                          🎥 Videos watched during this hour: {studentSessions.length}
                                        </p>
                                        <p className="text-xs text-purple-300 font-bold">
                                          Estimated traffic: {student.estimatedGB.toFixed(4)} GB
                                        </p>
                                      </div>

                                      <div className="mt-4 border border-gray-700 rounded-lg overflow-hidden bg-gray-950/50">
                                        <div className="px-3 py-2 bg-black/30 border-b border-gray-800">
                                          <h4 className="text-sm font-black text-orange-300">
                                            🎥 Videos Watched During This Hour
                                          </h4>
                                          <p className="text-[10px] text-gray-500">
                                            Sorted by the time the video session started.
                                          </p>
                                        </div>

                                        {studentSessions.length === 0 ? (
                                          <p className="p-3 text-xs text-gray-500 italic">
                                            No video session details found for this student in this hour.
                                          </p>
                                        ) : (
                                          <div className="divide-y divide-gray-800">
                                            {studentSessions.map((session, sessionIndex) => {
                                              const completion = Number(session.completionPercent || 0);
                                              const completed = completion >= 90;
                                              const videoInfo = getVideoInfo(session.videoPath);

                                              const sessionKey = `${studentKey}-session-${session.id || sessionIndex}`;
                                              const isSessionExpanded = expandedActivitySession === sessionKey;

                                              return (
                                                <div
                                                  key={session.id || `${studentKey}-session-${sessionIndex}`}
                                                  className="hover:bg-gray-900/70 transition"
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      setExpandedActivitySession(isSessionExpanded ? null : sessionKey)
                                                    }
                                                    className="w-full p-3 grid grid-cols-1 md:grid-cols-12 gap-3 text-left"
                                                  >
                                                    <div className="md:col-span-2">
                                                      <p className="text-xs font-black text-cyan-300">
                                                        {isSessionExpanded ? "▼" : "▶"} {formatSessionClockTime(session.startedAtMs || session.endedAtMs)}
                                                      </p>
                                                      <p className="text-[10px] text-gray-500">
                                                        #{sessionIndex + 1}
                                                      </p>
                                                    </div>

                                                    <div className="md:col-span-5 min-w-0">
                                                      <p className="text-sm font-bold text-white break-words">
                                                        {session.lessonTitle || videoInfo.lesson}
                                                      </p>
                                                      <p className="text-[10px] text-blue-300 mt-1 break-words">
                                                        📚 {session.courseId || videoInfo.course}
                                                      </p>
                                                    </div>

                                                    <div className="md:col-span-2">
                                                      <p className="text-[10px] text-gray-500 uppercase font-black">
                                                        Watched
                                                      </p>
                                                      <p className="text-sm font-black text-cyan-300">
                                                        {formatDuration(session.watchSeconds)}
                                                      </p>
                                                    </div>

                                                    <div className="md:col-span-1">
                                                      <p className="text-[10px] text-gray-500 uppercase font-black">
                                                        Done
                                                      </p>
                                                      <p className={`text-sm font-black ${completed ? "text-green-300" : "text-red-300"}`}>
                                                        {completed ? "✅" : "❌"} {completion}%
                                                      </p>
                                                    </div>

                                                    <div className="md:col-span-2 md:text-right">
                                                      <p className="text-xs text-purple-300">
                                                        {Number(session.estimatedGB || 0).toFixed(4)} GB
                                                      </p>
                                                      <p className="text-sm text-green-400 font-black">
                                                        {formatMoney(session.estimatedCost)}
                                                      </p>
                                                      <p className="text-[10px] text-gray-500 mt-1">
                                                        Click for details
                                                      </p>
                                                    </div>
                                                  </button>

                                                  {isSessionExpanded && (
                                                    <div className="mx-3 mb-3 bg-black/40 border border-gray-700 rounded-lg p-4">
                                                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                                                        <div className="min-w-0">
                                                          <h5 className="text-sm font-black text-orange-300 break-words">
                                                            🎬 Full Session Details
                                                          </h5>
                                                          <p className="text-xs text-white font-bold mt-1 break-words">
                                                            {session.lessonTitle || videoInfo.lesson}
                                                          </p>
                                                          <p className="text-[10px] text-blue-300 mt-1 break-words">
                                                            📚 {session.courseId || videoInfo.course}
                                                          </p>
                                                        </div>

                                                        <div className="text-left md:text-right flex-shrink-0">
                                                          <p className={`text-xs font-black ${completed ? "text-green-300" : "text-red-300"}`}>
                                                            {completed ? "✅ Completed" : "❌ Incomplete"}
                                                          </p>
                                                          <p className="text-[10px] text-gray-500">
                                                            Reason: {session.reason || "Unknown"}
                                                          </p>
                                                        </div>
                                                      </div>

                                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Started</p>
                                                          <p className="text-sm font-black text-cyan-300">{formatSessionFullTime(session.startedAtMs)}</p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Ended</p>
                                                          <p className="text-sm font-black text-cyan-300">{formatSessionFullTime(session.endedAtMs)}</p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Watch Time</p>
                                                          <p className="text-sm font-black text-cyan-300">{formatDuration(session.watchSeconds)}</p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Total Elapsed</p>
                                                          <p className="text-sm font-black text-purple-300">
                                                            {getSessionElapsedSeconds(session)
                                                              ? formatDuration(
                                                                  getSessionElapsedSeconds(session)
                                                                )
                                                              : "Unknown"}
                                                          </p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Completion</p>
                                                          <p className={`text-sm font-black ${completed ? "text-green-300" : "text-red-300"}`}>{completion}%</p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Play Count</p>
                                                          <p className="text-sm font-black text-white">{session.playCount || 0}</p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Pause Count</p>
                                                          <p className="text-sm font-black text-white">{session.pauseCount || 0}</p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Seek Count</p>
                                                          <p className="text-sm font-black text-white">{session.seekCount || 0}</p>
                                                        </div>

                                                        <div className="bg-gray-950/80 border border-gray-700 rounded-lg p-3">
                                                          <p className="text-[10px] text-gray-500 uppercase font-black">Traffic</p>
                                                          <p className="text-sm font-black text-purple-300">{Number(session.estimatedGB || 0).toFixed(4)} GB</p>
                                                        </div>
                                                      </div>

                                                      <div className="mt-3 bg-gray-950/80 border border-gray-700 rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                                        <p className="text-xs text-gray-400 break-all">
                                                          Path: {session.videoPath || "No video path saved"}
                                                        </p>
                                                        <p className="text-sm text-green-400 font-black flex-shrink-0">
                                                          Bunny Cost: {formatMoney(session.estimatedCost)}
                                                        </p>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>


                                      <div className="mt-4 border border-gray-700 rounded-lg overflow-hidden bg-gray-950/50">
                                        <div className="px-3 py-2 bg-black/30 border-b border-gray-800">
                                          <h4 className="text-sm font-black text-lime-300">
                                            📜 Activity Timeline
                                          </h4>
                                          <p className="text-[10px] text-gray-500">
                                            Chronological view of what happened during this hour. Pause and seek events are summarized from the saved session data.
                                          </p>
                                        </div>

                                        {timelineEvents.length === 0 ? (
                                          <p className="p-3 text-xs text-gray-500 italic">
                                            No timeline events found for this student in this hour.
                                          </p>
                                        ) : (
                                          <div className="p-4 space-y-0">
                                            {timelineEvents.map((event, eventIndex) => (
                                              <div
                                                key={event.key}
                                                className="relative pl-8 pb-5 last:pb-0"
                                              >
                                                {eventIndex < timelineEvents.length - 1 && (
                                                  <div className="absolute left-[11px] top-7 bottom-0 w-px bg-gray-700" />
                                                )}

                                                <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-gray-900 border border-gray-600 flex items-center justify-center text-xs">
                                                  {event.icon}
                                                </div>

                                                <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
                                                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                                                    <div className="min-w-0">
                                                      <p className="text-xs font-black text-cyan-300">
                                                        {formatSessionClockTime(event.timeMs)}
                                                      </p>
                                                      <p className={`text-sm font-black ${event.statusClass}`}>
                                                        {event.title}
                                                      </p>
                                                      <p className="text-xs text-white font-bold mt-1 break-words">
                                                        {event.lessonTitle}
                                                      </p>
                                                      <p className="text-[10px] text-blue-300 mt-1 break-words">
                                                        📚 {event.courseTitle}
                                                      </p>
                                                    </div>

                                                    <div className="text-left md:text-right flex-shrink-0">
                                                      <p className="text-[11px] text-gray-300">
                                                        {event.details}
                                                      </p>
                                                      {event.reason && (
                                                        <p className="text-[10px] text-gray-500 mt-1">
                                                          Reason: {event.reason}
                                                        </p>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}

          <div className="space-y-6">
            <div className="bg-gray-900/70 border border-gray-700 rounded-xl p-4">
              <h3 className="text-lg font-bold text-green-400 mb-3">
                👥 Student Watch Time
              </h3>
              {analyticsStudentRows.length === 0 ? (
                <p className="text-gray-500 italic">No student watch time yet.</p>
              ) : (
                <div className="space-y-3 max-h-[245px] overflow-y-auto pr-2 custom-scrollbar">
                  {analyticsStudentRows.map((student, index) => (
                    <div
                      key={student.uid || student.email}
                      className="bg-black/30 border border-gray-700 rounded-lg p-3"
                    >
                      <div className="flex justify-between gap-3">
                        <div className="overflow-hidden">
                          <p className="text-sm font-bold text-white truncate">
                            #{index + 1} {student.email}
                          </p>
                          <p className="text-xs text-gray-500">
                            {student.sessions} sessions • {student.estimatedGB.toFixed(4)} GB
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-black text-cyan-300">
                            {formatDuration(student.watchSeconds)}
                          </p>
                          <p className="text-xs text-green-400">
                            {formatMoney(student.estimatedCost)}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => openStudentHistory(student)}
                        className="mt-3 w-full py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                      >
                        👤 View Full Watch History
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gray-900/70 border border-gray-700 rounded-xl p-4">
              <h3 className="text-lg font-bold text-blue-400 mb-3">
                📹 Course Watch Time
              </h3>
              {analyticsCourseRows.length === 0 ? (
                <p className="text-gray-500 italic">No course watch time yet.</p>
              ) : (
                <div className="space-y-3 max-h-[245px] overflow-y-auto pr-2 custom-scrollbar">
                  {analyticsCourseRows.map((course, index) => (
                    <div
                      key={course.course}
                      className="bg-black/30 border border-gray-700 rounded-lg p-3 flex justify-between gap-3"
                    >
                      <div className="overflow-hidden">
                        <p className="text-sm font-bold text-white truncate">
                          #{index + 1} {course.course}
                        </p>
                        <p className="text-xs text-gray-500">
                          {course.studentCount} students • {course.sessions} sessions
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-black text-cyan-300">
                          {formatDuration(course.watchSeconds)}
                        </p>
                        <p className="text-xs text-green-400">
                          {formatMoney(course.estimatedCost)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-900/70 border border-gray-700 rounded-xl p-4">
          <h3 className="text-lg font-bold text-green-400 mb-3">
            💰 Bunny Cost Analytics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 uppercase font-bold">App Est. Traffic</p>
              <p className="text-2xl font-black text-purple-300">
                {analyticsEstimatedGB.toFixed(4)} GB
              </p>
            </div>
            <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 uppercase font-bold">App Est. Cost</p>
              <p className="text-2xl font-black text-green-400">
                {formatMoney(analyticsEstimatedCost)}
              </p>
            </div>
            <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 uppercase font-bold">Bunny Actual Traffic</p>
              <p className="text-2xl font-black text-emerald-300">
                {formatGB(bunnyActualGB)}
              </p>
            </div>
            <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 uppercase font-bold">Bunny Cost</p>
              <p className="text-2xl font-black text-green-300">
                {formatMoney(bunnyActualCost)}
              </p>
            </div>
            <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 uppercase font-bold">Cost / GB</p>
              <p className="text-2xl font-black text-orange-400">$0.03</p>
            </div>
            <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 uppercase font-bold">Note</p>
              <p className="text-xs text-gray-400 mt-1">
                Student and course costs are still estimated. Overall Bunny traffic is now real API data.
              </p>
            </div>
          </div>
        </div>
      </section>

        </>
      )}

      {activeAnalyticsPage === "watchCost" && (
        <>
      <section className="mb-10 bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-emerald-400">
              📊 Watch Analytics & Bunny Cost
            </h2>
            <p className="text-gray-400 text-xs">
              Based on saved videoSessions. Bunny estimate uses $0.03 per GB.
            </p>
          </div>

          <div className="flex flex-col items-end gap-1">
            <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-3 py-1 rounded-full font-bold">
              {uniqueVideoSessions.length} sessions
            </span>
            {totalDuplicateSessions > 0 && (
              <span className="text-[10px] bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 px-3 py-1 rounded-full font-bold">
                {totalDuplicateSessions} duplicates ignored
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Total Watch Time
            </p>
            <p className="text-2xl font-black text-white">
              {formatDuration(totalWatchSeconds)}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Avg Completion
            </p>
            <p className="text-2xl font-black text-blue-400">
              {averageCompletion}%
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Sessions Today
            </p>
            <p className="text-2xl font-black text-emerald-400">
              {sessionsToday.length}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Est. Total GB
            </p>
            <p className="text-2xl font-black text-purple-400">
              {totalEstimatedGB.toFixed(4)}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Est. Total Cost
            </p>
            <p className="text-2xl font-black text-green-400">
              {formatMoney(totalEstimatedCost)}
            </p>
          </div>

          <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
            <p className="text-gray-400 text-xs uppercase font-bold">
              Today Cost
            </p>
            <p className="text-2xl font-black text-orange-400">
              {formatMoney(estimatedCostToday)}
            </p>
            <p className="text-[10px] text-gray-500">
              {estimatedGBToday.toFixed(4)} GB today
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="bg-gray-900/70 border border-gray-700 rounded-xl p-4">
            <h3 className="text-lg font-bold text-green-400 mb-3">
              🏆 Top Cost Students
            </h3>

            {topCostStudents.length === 0 ? (
              <p className="text-gray-500 italic">No watch cost data yet.</p>
            ) : (
              <div className="space-y-3 max-h-[320px] overflow-y-auto pr-2 custom-scrollbar">
                {topCostStudents.map((student, index) => (
                  <div
                    key={student.uid || student.email}
                    className="bg-black/30 border border-gray-700 rounded-lg p-3"
                  >
                    <div className="flex justify-between gap-3">
                      <div className="overflow-hidden">
                        <p className="text-sm font-bold text-white truncate">
                          #{index + 1} {student.email}
                        </p>
                        <p className="text-xs text-gray-400">
                          Watch time: {formatDuration(student.watchSeconds)}
                        </p>
                        <p className="text-xs text-purple-300">
                          Traffic: {student.estimatedGB.toFixed(4)} GB
                        </p>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-black text-green-400">
                          {formatMoney(student.estimatedCost)}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          {student.sessions} sessions
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => openStudentHistory(student)}
                      className="mt-3 w-full py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                    >
                      👤 View Full Watch History
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gray-900/70 border border-gray-700 rounded-xl p-4">
            <h3 className="text-lg font-bold text-emerald-400 mb-3">
              🎥 Recent Watch Sessions
            </h3>

            {recentVideoSessions.length === 0 ? (
              <p className="text-gray-500 italic">No video sessions yet.</p>
            ) : (
              <div className="space-y-3 max-h-[320px] overflow-y-auto pr-2 custom-scrollbar">
                {recentVideoSessions.map((session) => (
                  <div
                    key={session.id}
                    className="bg-black/30 border border-gray-700 rounded-lg p-3"
                  >
                    <div className="flex flex-col md:flex-row md:justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-orange-400">
                          {session.email || "Unknown student"}
                        </p>

                        <p className="text-sm text-gray-200 mt-1">
                          {session.lessonTitle ||
                            getVideoInfo(session.videoPath).lesson}
                        </p>

                        <p className="text-[10px] text-blue-300 mt-1">
                          📚 {getVideoInfo(session.videoPath).course}
                        </p>

                        <p className="text-[10px] text-gray-500 mt-1 break-all">
                          {session.videoPath}
                        </p>
                      </div>

                      <div className="text-left md:text-right flex-shrink-0">
                        <p className="text-xs text-white font-bold">
                          Watched: {formatDuration(session.watchSeconds)}
                        </p>
                        <p className="text-xs text-blue-300">
                          Completion: {session.completionPercent || 0}%
                        </p>
                        <p className="text-xs text-purple-300">
                          {Number(session.estimatedGB || 0).toFixed(4)} GB
                        </p>
                        <p className="text-xs text-green-400 font-bold">
                          {formatMoney(session.estimatedCost)}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          Seek: {session.seekCount || 0} | Pause:{" "}
                          {session.pauseCount || 0}
                        </p>

                        <div className="grid grid-cols-1 gap-2 mt-3">
                          <button
                            type="button"
                            onClick={() => openStudentHistory(session)}
                            className="w-full py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                          >
                            👤 View Full Watch History
                          </button>

                          <button
                            type="button"
                            onClick={() => setSelectedSession(session)}
                            className="w-full py-2 rounded-md bg-emerald-900/30 border border-emerald-600/50 hover:bg-emerald-700 text-xs font-black transition text-emerald-200"
                          >
                            🔎 View Full Session Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      

        </>
      )}

      {activeAnalyticsPage === "lifetimeOpens" && (
        <>
      <section className="mb-10 bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-blue-400">
              📈 Lifetime Video Opens
            </h2>
            <p className="text-gray-400 text-xs">
              Top students based on lifetime signed video opens.
            </p>
          </div>

          <span className="text-xs bg-blue-500/20 text-blue-300 border border-blue-500/40 px-3 py-1 rounded-full font-bold">
            Top 100
          </span>
        </div>

        {topVideoStudents.length === 0 ? (
          <p className="text-gray-500 italic">No video open data yet.</p>
        ) : (
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2 custom-scrollbar">
            {topVideoStudents.map((student, index) => (
              <div
                key={student.id || student.email}
                className="bg-gray-900 border border-gray-700 rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 hover:border-blue-500/40 transition"
              >
                <div>
                  <p className="font-bold text-white">
                    #{index + 1} {student.email || "Unknown student"}
                  </p>

                  <p className="text-xs text-gray-500 mt-1">
                    🌍 {student.country || "Unknown"} | IP:{" "}
                    {student.ip || "Unknown"}
                  </p>

                  <p className="text-xs text-blue-300 mt-1">
                    🎥 Last video:{" "}
                    {student.currentVideo
                      ? getVideoInfo(student.currentVideo).lesson
                      : "None"}
                  </p>
                </div>

                <div className="text-left md:text-right">
                  <p className="text-3xl font-black text-blue-400">
                    {student.videoOpenCount || 0}
                  </p>

                  <p className="text-xs text-gray-500 uppercase font-bold">
                    video opens
                  </p>

                  <button
                    onClick={() => openStudentHistory(student)}
                    className="mt-3 w-full md:w-auto px-4 py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                  >
                    👤 View Full Watch History
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

        </>
      )}

      {activeAnalyticsPage === "recentActivity" && (
        <>
      <section className="mb-10 bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">
              🎥 Recent Video Activity
            </h2>
            <p className="text-gray-400 text-xs">
              Shows the latest videos opened by students.
            </p>
          </div>

          <span className="text-xs bg-orange-500/20 text-orange-300 border border-orange-500/40 px-3 py-1 rounded-full font-bold">
            {videoLogs.length} total logs
          </span>
        </div>

        {recentVideoLogs.length === 0 ? (
          <p className="text-gray-500 italic">No video activity yet.</p>
        ) : (
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2 custom-scrollbar">
            {recentVideoLogs.map((log) => {
              const matchingStudent = getStudentFromLog(log);

              return (
                <div
                  key={log.id}
                  className="bg-gray-900/70 border border-gray-700 rounded-xl p-4 hover:border-orange-500/40 transition"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                    <div>
                      <p className="text-sm font-bold text-orange-400">
                        {log.email || "Unknown student"}
                      </p>

                      <p className="text-sm text-gray-200 mt-1">
                        {getVideoInfo(log.videoPath).lesson}
                      </p>

                      <p className="text-[10px] text-blue-300 mt-1">
                        📚 {getVideoInfo(log.videoPath).course}
                      </p>

                      <p className="text-[10px] text-gray-500 break-all mt-1">
                        {log.videoPath}
                      </p>

                      <p className="text-[10px] text-gray-500 mt-1">
                        🌍 {log.country || "Unknown"} | IP:{" "}
                        {log.ip || "Unknown"}
                      </p>

                      {matchingStudent && (
                        <button
                          onClick={() => openStudentHistory(matchingStudent)}
                          className="mt-3 px-4 py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                        >
                          👤 View Full Watch History
                        </button>
                      )}
                    </div>

                    <p className="text-[11px] text-gray-400 whitespace-nowrap">
                      {formatFirestoreTime(log.openedAt || log.watchedAt)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

        </>
      )}

      {activeAnalyticsPage === "loginWatchHistory" && (
        <>
          <section className="mb-10 bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-700">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-blue-400">
                  👥 Login & Watch History
                </h2>
                <p className="text-gray-400 text-xs">
                  Uses your old working date logic. This view only adds login/watch summaries without changing the existing Analytics Center filters.
                </p>
              </div>

              <span className="text-xs bg-blue-500/20 text-blue-300 border border-blue-500/40 px-3 py-1 rounded-full font-bold w-fit">
                {loginWatchWindow.label} Report
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: "today", label: "📅 Today" },
                { key: "yesterday", label: "📅 Yesterday" },
                { key: "last7", label: "📅 Last 7 Days" },
                { key: "last30", label: "📅 Last 30 Days" },
                { key: "lifetime", label: "📅 Lifetime" },
                { key: "date", label: "🗓 Choose Date" },
              ].map((option) => (
                <button
                  key={option.key}
                  onClick={() => setLoginWatchRange(option.key)}
                  className={`px-4 py-2 rounded-lg text-xs font-black border transition ${
                    loginWatchRange === option.key
                      ? "bg-blue-600 border-blue-400 text-white"
                      : "bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {loginWatchRange === "date" && (
              <div className="mb-6">
                <input
                  type="date"
                  value={loginWatchDate}
                  onChange={(e) => setLoginWatchDate(e.target.value)}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 mb-6">
              <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
                <p className="text-gray-400 text-xs uppercase font-bold">Logged In</p>
                <p className="text-2xl font-black text-blue-300">{displayedLoggedInUsersForRange.length}</p>
              </div>

              <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
                <p className="text-gray-400 text-xs uppercase font-bold">Students Watched</p>
                <p className="text-2xl font-black text-cyan-300">{loginWatchStudentRows.length}</p>
              </div>

              <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
                <p className="text-gray-400 text-xs uppercase font-bold">Video Opens</p>
                <p className="text-2xl font-black text-orange-300">{loginWatchVideoOpens.length}</p>
              </div>

              <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
                <p className="text-gray-400 text-xs uppercase font-bold">Watch Time</p>
                <p className="text-2xl font-black text-green-300">{formatDuration(loginWatchTotalSeconds)}</p>
              </div>

              <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
                <p className="text-gray-400 text-xs uppercase font-bold">Est. Traffic</p>
                <p className="text-2xl font-black text-purple-300">{formatGB(loginWatchTotalGB)}</p>
              </div>

              <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
                <p className="text-gray-400 text-xs uppercase font-bold">Est. Cost</p>
                <p className="text-2xl font-black text-emerald-300">{formatMoney(loginWatchTotalCost)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
                <h3 className="text-lg font-black text-blue-300 mb-3">
                  👤 Logged In Students
                </h3>

                {displayedLoggedInUsersForRange.length === 0 ? (
                  <p className="text-gray-500 italic text-sm">No logins found for this range.</p>
                ) : (
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
                    {displayedLoggedInUsersForRange.map((student) => (
                      <div key={student.id || student.email} className="bg-black/30 border border-gray-700 rounded-lg p-3">
                        <p className="text-sm font-bold text-white break-all">{student.email}</p>
                        <p className="text-[11px] text-orange-300 mt-1">
                          Last login/activity: {formatLastLogin(student.lastLogin || student.activeSessionStartedAt || student.lastSeen)}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          🌍 {student.country || "Unknown"} • IP: {student.ip || "Unknown"}
                        </p>

                        <button
                          type="button"
                          onClick={() => openStudentHistory(student)}
                          className="mt-3 w-full py-2 rounded-md bg-blue-900/30 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200"
                        >
                          👤 View Full Watch History
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
                <h3 className="text-lg font-black text-cyan-300 mb-3">
                  🎥 Students Who Watched
                </h3>

                {loginWatchUsingOpenLogsFallback && (
                  <div className="mb-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 p-3 rounded-lg text-xs">
                    Showing students from video-open logs because detailed watch sessions for this range were not loaded. Watch time, traffic, and cost may stay 0 until more videoSessions are loaded.
                  </div>
                )}

                {loginWatchStudentRows.length === 0 ? (
                  <p className="text-gray-500 italic text-sm">No watch activity found for this range.</p>
                ) : (
                  <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
                    {loginWatchStudentRows.map((row) => {
                      const latest = row.latestSession;
                      const latestVideo = getVideoInfo(latest?.videoPath);

                      return (
                        <div key={row.uid || row.email} className="bg-black/30 border border-gray-700 rounded-lg p-3">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div>
                              <p className="text-sm font-bold text-white break-all">{row.email}</p>
                              <p className="text-[11px] text-gray-400 mt-1">
                                Latest: {latest?.lessonTitle || latestVideo.lesson}
                              </p>
                              <p className="text-[11px] text-blue-300">
                                📚 {latest?.courseId || latestVideo.course}
                              </p>
                              <p className="text-[10px] text-gray-500 mt-1 break-all">
                                {latest?.videoPath || "No video path saved"}
                              </p>
                            </div>

                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={() => openStudentHistory(row)}
                                className="px-3 py-2 rounded-md bg-blue-900/40 border border-blue-600/50 hover:bg-blue-700 text-xs font-black transition text-blue-200 w-fit"
                              >
                                👤 Full Watch History
                              </button>

                              {latest?.sourceType !== "open-log" && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedSession(latest)}
                                  className="px-3 py-2 rounded-md bg-emerald-900/40 border border-emerald-600/50 hover:bg-emerald-700 text-xs font-black transition text-emerald-200 w-fit"
                                >
                                  🔎 Latest Session Details
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 text-xs">
                            <div>
                              <p className="text-gray-500 font-bold">Videos</p>
                              <p className="text-white font-black">{row.videoCount}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 font-bold">Sessions</p>
                              <p className="text-white font-black">{row.sessions}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 font-bold">Watched</p>
                              <p className="text-green-300 font-black">{formatDuration(row.watchSeconds)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 font-bold">Traffic</p>
                              <p className="text-purple-300 font-black">{formatGB(row.estimatedGB)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 font-bold">Cost</p>
                              <p className="text-emerald-300 font-black">{formatMoney(row.estimatedCost)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}

        </>
      )}


      {selectedBandwidthVideo && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 p-6 border-b border-gray-700 bg-gray-800">
              <div>
                <h2 className="text-2xl font-black text-white">
                  🎥 Video Bandwidth Investigation
                </h2>

                <p className="text-orange-400 font-bold mt-1">
                  {selectedBandwidthVideo.lessonTitle || "Unknown video"}
                </p>

                <p className="text-xs text-blue-300 mt-1">
                  📚 {selectedBandwidthVideo.course || "Unknown Course"}
                </p>

                <p className="text-xs text-gray-500 mt-1 break-all">
                  {selectedBandwidthVideo.videoPath || "No path saved"}
                </p>

                <p className="text-xs text-gray-400 mt-2">
                  Showing data for: {getAnalyticsRangeLabel(analyticsRange)}
                </p>
              </div>

              <button
                onClick={() => setSelectedBandwidthVideo(null)}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-bold transition"
              >
                Close
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] custom-scrollbar space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Bandwidth</p>
                  <p className="text-2xl font-black text-purple-300">
                    {formatGB(selectedBandwidthVideoTotals.estimatedGB)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Cost</p>
                  <p className="text-2xl font-black text-green-400">
                    {formatMoney(selectedBandwidthVideoTotals.estimatedCost)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Watch Time</p>
                  <p className="text-2xl font-black text-white">
                    {formatDuration(selectedBandwidthVideoTotals.watchSeconds)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Sessions</p>
                  <p className="text-2xl font-black text-orange-400">
                    {selectedBandwidthVideoTotals.sessions}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Students</p>
                  <p className="text-2xl font-black text-blue-400">
                    {selectedBandwidthVideoTotals.students.size}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Avg Completion</p>
                  <p className="text-2xl font-black text-cyan-300">
                    {selectedBandwidthVideoAverageCompletion}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <h3 className="text-lg font-black text-orange-300 mb-3">
                    👥 Students Who Watched This Video
                  </h3>

                  {selectedBandwidthVideoStudentRows.length === 0 ? (
                    <p className="text-gray-500 italic text-sm">
                      No student data found for this video in this range.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-[520px] overflow-y-auto pr-2 custom-scrollbar">
                      {selectedBandwidthVideoStudentRows.map((student, index) => (
                        <button
                          key={student.uid || student.email || index}
                          type="button"
                          onClick={() => {
                            setSelectedBandwidthStudent(student);
                            setSelectedBandwidthVideo(null);
                          }}
                          className="w-full text-left bg-black/30 border border-gray-700 hover:border-cyan-500 rounded-lg p-3 transition"
                        >
                          <div className="flex justify-between gap-3">
                            <div className="overflow-hidden">
                              <p className="text-sm font-black text-white truncate">
                                #{index + 1} {student.email}
                              </p>
                              <p className="text-xs text-cyan-300 mt-1">
                                ⏱ {formatDuration(student.watchSeconds)} watched
                              </p>
                              <p className="text-[10px] text-gray-500 mt-1">
                                {student.sessions} sessions • {student.completedSessions} completed • {student.averageCompletion}% avg completion
                              </p>
                            </div>

                            <div className="text-right flex-shrink-0">
                              <p className="text-lg font-black text-purple-300">
                                {formatGB(student.estimatedGB)}
                              </p>
                              <p className="text-xs text-green-400 font-bold">
                                {formatMoney(student.estimatedCost)}
                              </p>
                            </div>
                          </div>

                          <p className="text-[10px] text-cyan-200 mt-3 font-bold">
                            Click to open this student’s bandwidth profile →
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <h3 className="text-lg font-black text-cyan-300 mb-3">
                    🕒 Video Session Timeline
                  </h3>

                  {selectedBandwidthVideoSessions.length === 0 ? (
                    <p className="text-gray-500 italic text-sm">
                      No sessions found for this video in this range.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-[520px] overflow-y-auto pr-2 custom-scrollbar">
                      {selectedBandwidthVideoSessions.map((session, index) => {
                        const studentEmail = session.email || "Unknown student";
                        return (
                          <div
                            key={session.id || session.sessionId || index}
                            className="bg-black/30 border border-gray-700 rounded-lg p-3"
                          >
                            <div className="flex justify-between gap-3">
                              <div className="overflow-hidden">
                                <p className="text-sm font-black text-white truncate">
                                  {studentEmail}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-1">
                                  Started:{" "}
                                  {formatSessionDateTime(
                                    session.startedAtMs || session.startedAt
                                  )}
                                  <br />
                                  Ended:{" "}
                                  {formatSessionDateTime(
                                    session.endedAtMs || session.endedAt
                                  )}
                                  <br />
                                  Total:{" "}
                                  {getSessionElapsedSeconds(session)
                                    ? formatDuration(
                                        getSessionElapsedSeconds(session)
                                      )
                                    : "Not recorded"}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-1">
                                  Reason: {session.reason || "Unknown"}
                                </p>
                              </div>

                              <div className="text-right flex-shrink-0">
                                <p className="text-sm font-black text-purple-300">
                                  {formatGB(session.estimatedGB)}
                                </p>
                                <p className="text-xs text-green-400 font-bold">
                                  {formatMoney(session.estimatedCost)}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[11px]">
                              <p className="text-cyan-300 font-bold">
                                ⏱ {formatDuration(session.watchSeconds)}
                              </p>
                              <p className="text-blue-300 font-bold">
                                ✅ {session.completionPercent || 0}%
                              </p>
                              <p className="text-yellow-300 font-bold">
                                ⏸ {session.pauseCount || 0} pause
                              </p>
                              <p className="text-red-300 font-bold">
                                ↔ {session.seekCount || 0} seek
                              </p>
                            </div>

                            <button
                              onClick={() => setSelectedSession(session)}
                              className="mt-3 px-3 py-2 rounded-md bg-emerald-900/30 border border-emerald-600/50 hover:bg-emerald-700 text-xs font-black transition text-emerald-200"
                            >
                              View Full Session Details
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedBandwidthStudent && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 p-6 border-b border-gray-700 bg-gray-800">
              <div>
                <h2 className="text-2xl font-black text-white">
                  👤 Student Bandwidth Profile
                </h2>

                <p className="text-cyan-400 font-bold mt-1">
                  {selectedBandwidthStudent.email || "Unknown student"}
                </p>

                <p className="text-xs text-gray-500 mt-1 break-all">
                  UID: {selectedBandwidthStudent.uid || selectedBandwidthStudentProfile?.id || "Unknown"}
                </p>

                <p className="text-xs text-gray-400 mt-2">
                  Showing data for: {getAnalyticsRangeLabel(analyticsRange)}
                </p>
              </div>

              <button
                onClick={() => setSelectedBandwidthStudent(null)}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-bold transition"
              >
                Close
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] custom-scrollbar space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Bandwidth</p>
                  <p className="text-2xl font-black text-purple-300">
                    {formatGB(selectedBandwidthStudentTotals.estimatedGB)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Cost</p>
                  <p className="text-2xl font-black text-green-400">
                    {formatMoney(selectedBandwidthStudentTotals.estimatedCost)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Watch Time</p>
                  <p className="text-2xl font-black text-white">
                    {formatDuration(selectedBandwidthStudentTotals.watchSeconds)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Sessions</p>
                  <p className="text-2xl font-black text-orange-400">
                    {selectedBandwidthStudentTotals.sessions}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Videos</p>
                  <p className="text-2xl font-black text-blue-400">
                    {selectedBandwidthStudentVideoRows.length}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">Avg Completion</p>
                  <p className="text-2xl font-black text-cyan-300">
                    {selectedBandwidthStudentAverageCompletion}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-xs text-gray-400 uppercase font-bold">Country</p>
                  <p className="text-lg font-black text-white mt-1">
                    🌍 {selectedBandwidthStudentCountry}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-xs text-gray-400 uppercase font-bold">Latest IP</p>
                  <p className="text-lg font-black text-white mt-1 break-all">
                    {selectedBandwidthStudentIp}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-xs text-gray-400 uppercase font-bold">Risk</p>
                  <p
                    className={`text-lg font-black mt-1 ${
                      selectedBandwidthStudentRiskLevel === "High"
                        ? "text-red-400"
                        : selectedBandwidthStudentRiskLevel === "Watch"
                        ? "text-yellow-300"
                        : "text-green-400"
                    }`}
                  >
                    {selectedBandwidthStudentRiskLevel === "High"
                      ? "🚨 High Usage"
                      : selectedBandwidthStudentRiskLevel === "Watch"
                      ? "⚠ Watch"
                      : "✅ Normal"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <h3 className="text-lg font-black text-blue-300 mb-3">
                    📚 Every Video Watched
                  </h3>

                  {selectedBandwidthStudentVideoRows.length === 0 ? (
                    <p className="text-gray-500 italic text-sm">
                      No video details found for this student in this range.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
                      {selectedBandwidthStudentVideoRows.map((video, index) => (
                        <div
                          key={video.videoKey}
                          className="bg-black/30 border border-gray-700 rounded-lg p-3"
                        >
                          <div className="flex justify-between gap-3">
                            <div className="overflow-hidden">
                              <p className="text-sm font-black text-white truncate">
                                #{index + 1} {video.lessonTitle}
                              </p>
                              <p className="text-[10px] text-blue-300 mt-1 truncate">
                                📚 {video.course}
                              </p>
                              <p className="text-[10px] text-gray-500 break-all mt-1">
                                {video.videoPath || "No path saved"}
                              </p>
                            </div>

                            <div className="text-right flex-shrink-0">
                              <p className="text-lg font-black text-purple-300">
                                {formatGB(video.estimatedGB)}
                              </p>
                              <p className="text-xs text-green-400 font-bold">
                                {formatMoney(video.estimatedCost)}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[11px]">
                            <p className="text-cyan-300 font-bold">
                              ⏱ {formatDuration(video.watchSeconds)}
                            </p>
                            <p className="text-orange-300 font-bold">
                              🎥 {video.sessions} sessions
                            </p>
                            <p className="text-green-300 font-bold">
                              ✅ {video.completedSessions} completed
                            </p>
                            <p className="text-purple-300 font-bold">
                              {video.averageCompletion}% avg
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <h3 className="text-lg font-black text-cyan-300 mb-3">
                    🕒 Session Timeline
                  </h3>

                  {selectedBandwidthStudentSessions.length === 0 ? (
                    <p className="text-gray-500 italic text-sm">
                      No sessions found for this student in this range.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
                      {selectedBandwidthStudentSessions.map((session, index) => {
                        const videoInfo = getVideoInfo(session.videoPath);
                        return (
                          <div
                            key={session.id || session.sessionId || index}
                            className="bg-black/30 border border-gray-700 rounded-lg p-3"
                          >
                            <div className="flex justify-between gap-3">
                              <div className="overflow-hidden">
                                <p className="text-sm font-black text-white truncate">
                                  {session.lessonTitle || videoInfo.lesson}
                                </p>
                                <p className="text-[10px] text-blue-300 mt-1 truncate">
                                  📚 {session.courseId || videoInfo.course}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-1">
                                  Started:{" "}
                                  {formatSessionDateTime(
                                    session.startedAtMs || session.startedAt
                                  )}
                                  <br />
                                  Ended:{" "}
                                  {formatSessionDateTime(
                                    session.endedAtMs || session.endedAt
                                  )}
                                  <br />
                                  Total:{" "}
                                  {getSessionElapsedSeconds(session)
                                    ? formatDuration(
                                        getSessionElapsedSeconds(session)
                                      )
                                    : "Not recorded"}
                                </p>
                              </div>

                              <div className="text-right flex-shrink-0">
                                <p className="text-sm font-black text-purple-300">
                                  {formatGB(session.estimatedGB)}
                                </p>
                                <p className="text-xs text-green-400 font-bold">
                                  {formatMoney(session.estimatedCost)}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[11px]">
                              <p className="text-cyan-300 font-bold">
                                ⏱ {formatDuration(session.watchSeconds)}
                              </p>
                              <p className="text-blue-300 font-bold">
                                ✅ {session.completionPercent || 0}%
                              </p>
                              <p className="text-yellow-300 font-bold">
                                ⏸ {session.pauseCount || 0} pause
                              </p>
                              <p className="text-red-300 font-bold">
                                ↔ {session.seekCount || 0} seek
                              </p>
                            </div>

                            <button
                              onClick={() => setSelectedSession(session)}
                              className="mt-3 px-3 py-2 rounded-md bg-emerald-900/30 border border-emerald-600/50 hover:bg-emerald-700 text-xs font-black transition text-emerald-200"
                            >
                              View Full Session Details
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSession && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 p-6 border-b border-gray-700 bg-gray-800">
              <div>
                <h2 className="text-2xl font-black text-white">
                  🎥 Full Session Details
                </h2>

                <p className="text-orange-400 font-bold mt-1">
                  {selectedSession.email || "Unknown student"}
                </p>
              </div>

              <button
                onClick={() => setSelectedSession(null)}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-bold transition"
              >
                Close
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] custom-scrollbar space-y-4">
              <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                <p className="text-xs text-gray-400 uppercase font-bold">Video</p>

                <p className="text-lg font-bold text-white mt-1">
                  {selectedSession.lessonTitle ||
                    getVideoInfo(selectedSession.videoPath).lesson}
                </p>

                <p className="text-xs text-blue-300 mt-1">
                  📚 {getVideoInfo(selectedSession.videoPath).course}
                </p>

                <p className="text-[10px] text-gray-500 break-all mt-2">
                  {selectedSession.videoPath}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">
                    Watched
                  </p>
                  <p className="text-2xl font-black text-white">
                    {formatDuration(selectedSession.watchSeconds)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">
                    Completion
                  </p>
                  <p className="text-2xl font-black text-blue-400">
                    {selectedSession.completionPercent || 0}%
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">
                    Traffic
                  </p>
                  <p className="text-2xl font-black text-purple-400">
                    {Number(selectedSession.estimatedGB || 0).toFixed(4)} GB
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase font-bold">
                    Cost
                  </p>
                  <p className="text-2xl font-black text-green-400">
                    {formatMoney(selectedSession.estimatedCost)}
                  </p>
                </div>
              </div>

              <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-xs uppercase font-bold">Play</p>
                  <p className="text-xl font-black text-white">
                    {selectedSession.playCount || 0}
                  </p>
                </div>

                <div>
                  <p className="text-gray-400 text-xs uppercase font-bold">Pause</p>
                  <p className="text-xl font-black text-white">
                    {selectedSession.pauseCount || 0}
                  </p>
                </div>

                <div>
                  <p className="text-gray-400 text-xs uppercase font-bold">Seek</p>
                  <p className="text-xl font-black text-white">
                    {selectedSession.seekCount || 0}
                  </p>
                </div>
              </div>

              <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                <p className="text-xs text-gray-400">
                  Started:{" "}
                  {formatSessionDateTime(
                    selectedSession.startedAtMs ||
                      selectedSession.startedAt
                  )}
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  Ended:{" "}
                  {formatSessionDateTime(
                    selectedSession.endedAtMs ||
                      selectedSession.endedAt
                  )}
                </p>

                <p className="text-xs text-cyan-300 font-bold mt-1">
                  Total elapsed:{" "}
                  {getSessionElapsedSeconds(selectedSession)
                    ? formatDuration(
                        getSessionElapsedSeconds(selectedSession)
                      )
                    : "Not recorded"}
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  Video duration:{" "}
                  {formatDuration(selectedSession.durationSeconds)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedStudent && selectedStudentStats && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 p-6 border-b border-gray-700 bg-gray-800">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-black text-white">
                    👤 Student Full Watch History
                  </h2>
                  <span
                    className={`text-[10px] px-2 py-1 rounded-full border font-black ${
                      isStudentOnline(selectedStudent)
                        ? "bg-green-900/40 border-green-600 text-green-300"
                        : "bg-gray-900 border-gray-600 text-gray-400"
                    }`}
                  >
                    {isStudentOnline(selectedStudent) ? "● ONLINE" : "● OFFLINE"}
                  </span>
                </div>

                <p className="text-orange-400 font-bold mt-1">
                  {selectedStudent.email || "Unknown student"}
                </p>

                <p className="text-xs text-gray-500 mt-1 break-all">
                  UID: {selectedStudent.id}
                </p>

                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-gray-400">
                  <span>Joined: {formatCreatedDate(selectedStudent)}</span>
                  <span>Last login: {formatLastLogin(selectedStudent.lastLogin)}</span>
                  <span>Country: {selectedStudent.country || "Unknown"}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedStudent(null);
                  setSelectedSession(null);
                }}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-bold transition"
              >
                Close
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(92vh-135px)] custom-scrollbar">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">Sessions</p>
                  <p className="text-2xl font-black text-blue-400">
                    {selectedStudentSessions.length}
                  </p>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Opens: {selectedStudentStats.lifetime}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">Today</p>
                  <p className="text-2xl font-black text-red-400">
                    {selectedStudentSessionsToday.length}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">This Week</p>
                  <p className="text-2xl font-black text-purple-400">
                    {selectedStudentSessionsThisWeek.length}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">Watch Time</p>
                  <p className="text-xl font-black text-cyan-400">
                    {formatDuration(selectedStudentSessionTotals.watchSeconds)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">Completion</p>
                  <p className="text-2xl font-black text-yellow-400">
                    {selectedStudentAverageCompletion}%
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">Traffic</p>
                  <p className="text-lg font-black text-purple-300">
                    {formatGB(selectedStudentSessionTotals.estimatedGB)}
                  </p>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">Est. Cost</p>
                  <p className="text-xl font-black text-green-400">
                    {formatMoney(selectedStudentSessionTotals.estimatedCost)}
                  </p>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-3 mb-6">
                <div className="flex flex-wrap gap-2">
                  {[
                    ["all", "All"],
                    ["today", "Today"],
                    ["yesterday", "Yesterday"],
                    ["week", "This Week"],
                    ["last30", "Last 30 Days"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => setHistoryFilter(value)}
                      className={`px-4 py-2 rounded-lg text-xs font-black border transition ${
                        historyFilter === value
                          ? "bg-orange-600 border-orange-400 text-white"
                          : "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  placeholder="Search video, course, country, or IP..."
                  className="flex-1 bg-gray-800 border border-gray-700 p-2 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none transition"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-lg font-black text-white">🎥 Viewing Sessions</h3>
                  <p className="text-[11px] text-gray-500">
                    Showing {filteredStudentHistory.length} of {selectedStudentSessions.length} sessions.
                  </p>
                </div>
              </div>

              {studentHistoryLoading ? (
                <div className="bg-blue-900/20 border border-blue-700/50 rounded-xl p-4 mb-4">
                  <p className="text-blue-300 text-sm font-bold">
                    Loading this student's full watch history...
                  </p>
                </div>
              ) : null}

              {studentHistoryError ? (
                <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4 mb-4">
                  <p className="text-red-300 text-sm font-bold">
                    Could not load full watch history.
                  </p>
                  <p className="text-[11px] text-red-200/70 mt-1">
                    {studentHistoryError}
                  </p>
                </div>
              ) : null}

              {!studentHistoryLoading && selectedStudentSessions.length === 0 && selectedStudentStats.logs.length > 0 ? (
                <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-4 mb-4">
                  <p className="text-amber-300 text-sm font-bold">
                    Video-open history exists, but no detailed watch sessions were found.
                  </p>
                  <p className="text-[11px] text-amber-200/70 mt-1">
                    Older opens may not include watch time, completion, traffic, or player actions.
                  </p>
                </div>
              ) : null}

              {!studentHistoryLoading && filteredStudentHistory.length === 0 ? (
                <p className="text-gray-500 italic text-center py-10">
                  No detailed watch sessions found for this filter.
                </p>
              ) : (
                <div className="space-y-4">
                  {filteredStudentHistory.map((session, index) => {
                    const info = getVideoInfo(session.videoPath);
                    const lessonTitle = session.lessonTitle || info.lesson || "Unknown video";
                    const courseTitle = session.courseId || info.course || "Unknown course";
                    const startedAt = Number(session.startedAtMs || session.endedAtMs || 0);
                    const endedAt = Number(session.endedAtMs || 0);
                    const completion = Math.round(Number(session.completionPercent || 0));

                    return (
                      <div
                        key={session.id || `${session.uid || session.email}-${startedAt}-${index}`}
                        className="bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-orange-500/50 transition"
                      >
                        <div className="flex flex-col xl:flex-row xl:justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="text-base font-black text-orange-400 break-words">
                              🎥 {lessonTitle}
                            </p>

                            <p className="text-xs text-blue-300 mt-1">
                              📚 {courseTitle}
                            </p>

                            <p className="text-[10px] text-gray-500 break-all mt-1">
                              {session.videoPath || "No video path recorded"}
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1 mt-3 text-[11px] text-gray-300">
                              <p>
                                <span className="text-gray-500">Started:</span>{" "}
                                {startedAt ? new Date(startedAt).toLocaleString() : "Unknown"}
                              </p>
                              <p>
                                <span className="text-gray-500">Ended:</span>{" "}
                                {endedAt ? new Date(endedAt).toLocaleString() : "Not recorded"}
                              </p>
                              <p>
                                <span className="text-gray-500">Total elapsed:</span>{" "}
                                {getSessionElapsedSeconds(session)
                                  ? formatDuration(
                                      getSessionElapsedSeconds(session)
                                    )
                                  : "Not recorded"}
                              </p>
                              <p>
                                <span className="text-gray-500">Country:</span>{" "}
                                {session.country || selectedStudent.country || "Unknown"}
                              </p>
                              <p className="break-all">
                                <span className="text-gray-500">IP:</span>{" "}
                                {session.ip || selectedStudent.ip || "Unknown"}
                              </p>
                            </div>
                          </div>

                          <div className="xl:w-[390px] flex-shrink-0">
                            <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-2 gap-2">
                              <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
                                <p className="text-[9px] uppercase font-bold text-gray-500">Watch Time</p>
                                <p className="text-sm font-black text-cyan-300">
                                  {formatDuration(session.watchSeconds)}
                                </p>
                              </div>
                              <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
                                <p className="text-[9px] uppercase font-bold text-gray-500">Completion</p>
                                <p className={`text-sm font-black ${completion >= 90 ? "text-green-400" : "text-yellow-300"}`}>
                                  {completion}%
                                </p>
                              </div>
                              <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
                                <p className="text-[9px] uppercase font-bold text-gray-500">Traffic</p>
                                <p className="text-sm font-black text-purple-300">
                                  {formatGB(session.estimatedGB)}
                                </p>
                              </div>
                              <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
                                <p className="text-[9px] uppercase font-bold text-gray-500">Cost</p>
                                <p className="text-sm font-black text-green-400">
                                  {formatMoney(session.estimatedCost)}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2 mt-3 text-[10px] font-bold">
                              <span className="bg-blue-900/30 border border-blue-700/50 text-blue-300 rounded-full px-3 py-1">
                                ▶ Play {session.playCount || 0}
                              </span>
                              <span className="bg-yellow-900/30 border border-yellow-700/50 text-yellow-300 rounded-full px-3 py-1">
                                ⏸ Pause {session.pauseCount || 0}
                              </span>
                              <span className="bg-purple-900/30 border border-purple-700/50 text-purple-300 rounded-full px-3 py-1">
                                ⏩ Seek {session.seekCount || 0}
                              </span>
                            </div>

                            <button
                              onClick={() => setSelectedSession(session)}
                              className="mt-3 w-full py-2 rounded-lg bg-emerald-900/30 border border-emerald-600/50 hover:bg-emerald-700 text-xs font-black transition text-emerald-200"
                            >
                              🔎 View Full Session Details
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editingStudentAccount && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-purple-500/50 bg-gray-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-700 bg-purple-950/40 p-5">
              <div>
                <h2 className="text-xl font-black text-purple-200">
                  ✏️ Edit Student Account
                </h2>
                <p className="mt-1 text-xs text-gray-400">
                  UID: {editingStudentAccount.id}
                </p>
              </div>

              <button
                type="button"
                onClick={closeEditStudentAccount}
                disabled={editStudentSaving}
                className="rounded-lg bg-gray-700 px-3 py-2 text-sm font-black hover:bg-gray-600 disabled:opacity-50"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveStudentAccount} className="space-y-5 p-5">
              <div>
                <label className="mb-1 block text-sm font-bold text-gray-200">
                  Student Email
                </label>
                <input
                  type="email"
                  required
                  value={editStudentEmail}
                  onChange={(event) =>
                    setEditStudentEmail(event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 p-3 text-white outline-none focus:border-purple-500"
                />
                <p className="mt-1 text-[11px] text-yellow-300">
                  Changing this updates both Firebase Authentication and the
                  Firestore student profile. The student must use the new email
                  for future logins.
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-gray-200">
                  New Password
                </label>
                <input
                  type="password"
                  minLength={6}
                  value={editStudentPassword}
                  onChange={(event) =>
                    setEditStudentPassword(event.target.value)
                  }
                  placeholder="Leave blank to keep the current password"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 p-3 text-white outline-none focus:border-purple-500"
                />
                <p className="mt-1 text-[11px] text-gray-400">
                  Current passwords cannot be viewed. Enter at least 6
                  characters only when resetting it.
                </p>
              </div>

              <div className="rounded-xl border border-gray-700 bg-black/25 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                  Current subscription
                </p>
                <p className="mt-1 font-black text-white">
                  {editingStudentAccount.subscriptionPlanName ||
                    STUDENT_PLAN_OPTIONS[
                      editingStudentAccount.subscriptionPlan
                    ]?.name ||
                    (editingStudentAccount.subscriptionExempt
                      ? "Subscription exempt"
                      : "No plan recorded")}
                </p>

                <div className="mt-3 grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
                  <div>
                    <p className="text-gray-500">Account / subscription start</p>
                    <p className="font-bold text-cyan-300">
                      {getStudentSubscriptionStartMs(editingStudentAccount)
                        ? new Date(
                            getStudentSubscriptionStartMs(
                              editingStudentAccount
                            )
                          ).toLocaleString()
                        : "Unknown"}
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-500">Current expiration</p>
                    <p className="font-bold text-orange-300">
                      {getDateMs(
                        editingStudentAccount.subscriptionExpiresAt
                      ) ||
                      Number(
                        editingStudentAccount.subscriptionExpiresAtMs || 0
                      )
                        ? new Date(
                            getDateMs(
                              editingStudentAccount.subscriptionExpiresAt
                            ) ||
                              Number(
                                editingStudentAccount
                                  .subscriptionExpiresAtMs || 0
                              )
                          ).toLocaleString()
                        : "Not recorded"}
                    </p>
                  </div>
                </div>
              </div>

              {!editingStudentAccount.subscriptionExempt && (
                <div>
                  <label className="mb-1 block text-sm font-bold text-gray-200">
                    Upgrade Plan
                  </label>

                  <select
                    value={editStudentPlan}
                    onChange={(event) =>
                      setEditStudentPlan(event.target.value)
                    }
                    className="w-full rounded-lg border border-gray-700 bg-gray-800 p-3 text-white outline-none focus:border-purple-500"
                  >
                    <option value="">Keep Current Plan</option>
                    {getAvailableUpgradePlans(editingStudentAccount).map(
                      (planCode) => (
                        <option key={planCode} value={planCode}>
                          Upgrade to {STUDENT_PLAN_OPTIONS[planCode].name}
                        </option>
                      )
                    )}
                  </select>

                  {getAvailableUpgradePlans(editingStudentAccount).length ===
                    0 && (
                    <p className="mt-2 text-xs font-bold text-blue-300">
                      This student already has the highest available plan. No
                      downgrade options are allowed.
                    </p>
                  )}

                  {editStudentPlan &&
                    getStudentSubscriptionStartMs(editingStudentAccount) && (
                      <div className="mt-3 rounded-lg border border-green-500/40 bg-green-950/30 p-3">
                        <p className="text-xs text-gray-300">
                          New expiration calculated from the original account
                          start date:
                        </p>
                        <p className="mt-1 font-black text-green-300">
                          {addMonthsToDate(
                            getStudentSubscriptionStartMs(
                              editingStudentAccount
                            ),
                            STUDENT_PLAN_OPTIONS[editStudentPlan]?.months
                          )?.toLocaleString() || "Unable to calculate"}
                        </p>
                      </div>
                    )}
                </div>
              )}

              {editingStudentAccount.subscriptionExempt && (
                <div className="rounded-lg border border-yellow-500/40 bg-yellow-950/30 p-3 text-sm text-yellow-200">
                  This is a subscription-exempt lesson-checking account. Its
                  email and password can be changed, but a paid plan cannot be
                  assigned from this editor.
                </div>
              )}

              <div className="flex flex-col-reverse gap-3 border-t border-gray-700 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeEditStudentAccount}
                  disabled={editStudentSaving}
                  className="rounded-lg bg-gray-700 px-5 py-2 font-black hover:bg-gray-600 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={editStudentSaving}
                  className="rounded-lg bg-purple-600 px-5 py-2 font-black hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {editStudentSaving ? "Saving Changes..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
